"use client";

/**
 * ส่งบัญชี — Accounting Handoff area (แยกจาก 5 tabs งานบันทึก).
 *
 * หน้าแรก: ตารางรอบบัญชีตามแผน §3.1
 * Drawer: เตรียมส่งบัญชี from ข้อมูล (prototype entry) then the same
 *         prepare / review / export / history details.
 * Copy: สร้างไฟล์ ≠ ลงบัญชีเสร็จ (FE-5).
 *
 * Counts deep-link back to รายการธุรกรรม (same month + handoff status) so
 * ส่งบัญชี and the recording tabs stay reconcilable (A+B).
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useRouter } from "next/navigation";
import Link from "next/link";
import {
  ArrowLeft,
  CalendarDays,
  Check,
  ChevronDown,
  Download,
  FileSpreadsheet,
  History,
  Loader2,
  RefreshCw,
  Settings2,
} from "lucide-react";
import { ModalCloseButton, ModalShell } from "@aera/ui";
import { AeraButton } from "@/app/components/ui/AeraButton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/app/components/ui/DropdownMenu";
import { showToast } from "@/app/components/ui/Toast";
import { useAuthz } from "@/lib/authz";
import { billlinAuthUserMessage } from "@/lib/billlinAuthErrors";
import { signBilllinLink } from "@/lib/billlinSignedLinks";
import { createMarketApiFetch } from "@/lib/marketApiFetch";
import { EvidenceAccessProvider } from "@/lib/authz/EvidenceAccess";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/app/components/ui/Tooltip";
import { sanitizeInternalReturnTo } from "@/app/artifacts/table/parseTableUrl";
import { getUserIdFromAuthToken } from "@/lib/getUserIdFromAuthToken";
import { subscribeAuthSession } from "@/lib/authSession";
import { readCachedFeedItemsForWorkspace } from "@/hooks/useFinancialWorkFeed";
import {
  fetchPrepareDrawerWorkFeed,
  buildPrepareWorkItemHref,
  feedItemToPrepareIssue,
  filterFeedItemsForPeriod,
  countFeedItemsByBucket,
} from "@/lib/financialWork/prepareDrawerFeed";
import type { FinancialWorkFeedItem } from "@/lib/financialWork/types";
import {
  AccountingPreparePanel,
  type PrepareExportRow,
  type PrepareIssueGroup,
} from "./AccountingPreparePanel";
import { AccountingReviewContent } from "./review/AccountingReviewShell";

const marketApiFetch = createMarketApiFetch();

/** Job phases from GET /exports/{id} (plan §9.1) — polled in parallel with POST. */
type ExportJobPhase = "queued" | "validating" | "exporting" | "succeeded";

const EXPORT_PHASE_STEPS: {
  key: Exclude<ExportJobPhase, "succeeded">;
  label: string;
  /** Owner-facing line for the active step. */
  detail: string;
}[] = [
  {
    key: "queued",
    label: "รับคำขอ",
    detail: "รับคำขอสร้างชุดส่งบัญชีแล้ว กำลังเริ่มงาน",
  },
  {
    key: "validating",
    label: "เตรียมข้อมูล",
    detail: "กำลังรวบรวมรายรับ การจัดสรร รายจ่าย และรายการต้องตรวจของรอบนี้",
  },
  {
    key: "exporting",
    label: "สร้างไฟล์",
    detail: "กำลังสร้าง Excel + ZIP หลักฐานให้ผู้ทำบัญชี",
  },
];

const EXPORT_PHASE_INDEX: Record<ExportJobPhase, number> = {
  queued: 0,
  validating: 1,
  exporting: 2,
  succeeded: 3,
};

function phaseFromJobStatus(status: string): ExportJobPhase {
  if (status === "succeeded") return "succeeded";
  if (status === "validating") return "validating";
  if (status === "exporting") return "exporting";
  return "queued";
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

type ExportAdapterOption = {
  adapter_key: string;
  label: string;
  kind: string;
  description?: string;
};

type ExportPreview = {
  period: string;
  snapshot_version: number;
  valid: boolean;
  ready_to_export: boolean;
  adapter_key?: string;
  adapter_label?: string;
  adapter_kind?: string;
  adapters?: ExportAdapterOption[];
  issues: { code?: string; severity: string; message: string }[];
  sheet_counts: {
    money_in: number;
    allocations: number;
    sales: number;
    expenses: number;
    review_items: number;
    corrections: number;
  };
  period_summary?: {
    ready_count: number;
    needs_review_count: number;
    missing_evidence_count: number;
    correction_count: number;
  };
};

const SHEET_COUNT_ROWS: { key: keyof ExportPreview["sheet_counts"]; label: string }[] = [
  { key: "money_in", label: "รายรับ" },
  { key: "allocations", label: "การจัดสรรเงิน" },
  { key: "sales", label: "รายได้-รายการขาย" },
  { key: "expenses", label: "รายจ่าย" },
  { key: "review_items", label: "รายการต้องตรวจ" },
  { key: "corrections", label: "รายการแก้ไข" },
];

type PeriodExportSummary = {
  export_job_id: string;
  status: string;
  snapshot_version: number;
  requested_at: string | null;
};

type AccountingPeriod = {
  period: string;
  recorded_count: number;
  /** Visible รายรับ rows for the month (same rules as income tab). */
  income_recorded_count: number;
  /** Visible รายจ่าย rows for the month (same rules as expense tab). */
  expense_recorded_count: number;
  ready_count: number;
  needs_review_count: number;
  missing_evidence_count: number;
  correction_count: number;
  period_status: string;
  latest_export: PeriodExportSummary | null;
};

const THAI_MONTHS = [
  "ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.",
  "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค.",
];

const THAI_MONTHS_FULL = [
  "มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน",
  "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม",
];

function formatPeriodThai(period: string): string {
  const [y, m] = period.split("-");
  const month = THAI_MONTHS[Number(m) - 1] ?? m;
  return `${month} ${Number(y) + 543}`;
}

function formatPeriodThaiFull(period: string): string {
  const [y, m] = period.split("-");
  const month = THAI_MONTHS_FULL[Number(m) - 1] ?? m;
  return `${month} ${Number(y) + 543}`;
}

const RECORD_TYPE_LABEL: Record<string, string> = {
  customer_payment: "ชำระลูกหนี้",
  unclassified_receipt: "เงินโอน",
  customer_credit: "เครดิตลูกค้า",
  advance_receipt: "เงินมัดจำ/รับล่วงหน้า",
  refund_due: "ต้องคืนลูกค้า",
  expense: "รายจ่าย",
  correction: "การแก้ไข",
};

type DrawerHandoff = {
  handoff_record_id: string;
  record_type: string;
  source_type: string;
  source_id?: string;
  amount: string;
  classification_status: string;
  accounting_status?: string;
  review_reason: string | null;
  counterparty_json: { name?: string } | string | null;
};

/** Match /artifacts periods SQL: one row per draft source after a successful export. */
function periodCorrectionHandoffs(
  handoffs: DrawerHandoff[],
  periodHasSuccessfulExport: boolean,
): DrawerHandoff[] {
  if (!periodHasSuccessfulExport) return [];
  const bySource = new Map<string, DrawerHandoff>();
  for (const item of handoffs) {
    if (item.accounting_status !== "not_prepared") continue;
    // ต้องตรวจ / เอกสารขาด stay in 「ต้องให้คุณทำ」 — don't duplicate here.
    if (
      item.classification_status === "needs_review" ||
      item.classification_status === "missing_evidence"
    ) {
      continue;
    }
    const key = `${item.source_type}:${item.source_id || item.handoff_record_id}`;
    const existing = bySource.get(key);
    // Prefer the correction marker row when present (same source family).
    if (!existing || item.record_type === "correction") {
      bySource.set(key, item);
    }
  }
  return Array.from(bySource.values());
}

function counterpartyName(raw: DrawerHandoff["counterparty_json"]): string {
  if (!raw) return "";
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw) as { name?: string };
      return parsed.name || "";
    } catch {
      return "";
    }
  }
  return raw.name || "";
}

function formatHandoffAmount(raw: string): string {
  const n = Number(raw);
  if (Number.isNaN(n)) return raw;
  return `฿${n.toLocaleString("th-TH", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

function handoffIssueTitle(item: DrawerHandoff): string {
  const typeLabel =
    RECORD_TYPE_LABEL[item.record_type] ||
    (item.source_type === "expense" ? "รายจ่าย" : "เงินโอน");
  const name = counterpartyName(item.counterparty_json);
  const reason = item.review_reason?.trim() || "";
  if (item.record_type === "correction") {
    return name ? `แก้ไขหลังส่ง · ${typeLabel} · ${name}` : `แก้ไขหลังส่ง · ${typeLabel}`;
  }
  if (item.classification_status === "needs_review") {
    if (!name) return `${typeLabel} — ยังไม่มีชื่อคู่ค้า`;
    return reason ? `${typeLabel} · ${name} — ${reason}` : `${typeLabel} · ${name}`;
  }
  if (item.classification_status === "missing_evidence") {
    return `${typeLabel}${name ? ` · ${name}` : ""} — รอใบเสร็จ`;
  }
  return name ? `${typeLabel} · ${name}` : typeLabel;
}

function handoffPrepareSubtitle(item: DrawerHandoff): string | undefined {
  if (item.classification_status === "missing_evidence") {
    return "รอใบเสร็จ";
  }
  if (item.record_type === "unclassified_receipt") {
    return "สลิปจาก LINE";
  }
  if (item.record_type === "expense") {
    return item.source_type === "expense" ? "เงินสดหน้าร้าน" : "รายจ่าย";
  }
  if (item.source_type === "payment") {
    return "รายการธนาคาร";
  }
  const reason = item.review_reason?.trim();
  return reason || undefined;
}

export type AccountingPeriodsShellProps = {
  variant?: "page" | "drawer";
  onClose?: () => void;
  workspaceId?: string;
  serviceId?: string | null;
  ownerUserId?: string | null;
  returnTo?: string;
  initialPeriod?: string;
};

function formatExportWhen(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return iso.replace("T", " ").slice(0, 16);
  }
  return d.toLocaleString("th-TH", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function adapterLabel(adapterKey: string): string {
  if (adapterKey === "accountant_excel_v1" || /excel/i.test(adapterKey)) {
    return "Excel + ZIP";
  }
  if (adapterKey === "accountant_csv_v1" || /csv/i.test(adapterKey)) {
    return "CSV + ZIP";
  }
  if (adapterKey === "peak_v1") return "PEAK";
  if (adapterKey === "flowaccount_v1") return "FlowAccount";
  if (adapterKey === "provider_sim_v1") return "ทดลองส่ง (จำลอง)";
  return adapterKey || "—";
}

function isAdapterComingSoon(adapterKey: string): boolean {
  return adapterKey === "flowaccount_v1";
}

const PROVIDER_MODE_OPTIONS = [
  { value: "auto", label: "ส่งออกจริงเมื่อมีรหัสแล้ว" },
  { value: "sim", label: "ทดลองส่ง (ไม่ส่งออกจริง)" },
  { value: "live", label: "ส่งออกจริงเสมอ" },
] as const;

function primaryDownloadLabel(adapterKey: string, filename?: string): string {
  if (filename?.endsWith(".xlsx")) return "Excel";
  if (filename?.includes("CSV")) return "CSV";
  if (filename?.includes("ProviderReceipt") || filename?.endsWith(".json")) {
    return "ใบตอบรับ";
  }
  if (adapterKey === "accountant_csv_v1") return "CSV";
  if (
    adapterKey.startsWith("peak") ||
    adapterKey.startsWith("flow") ||
    adapterKey.includes("provider")
  ) {
    return "ใบตอบรับ";
  }
  return "ไฟล์";
}

/** Inclusive YYYY-MM-DD range for a period (matches FilterBar custom dates). */
function periodDateRange(period: string): { from: string; to: string } {
  const [ys, ms] = period.split("-");
  const y = Number(ys);
  const m = Number(ms);
  const lastDay = new Date(y, m, 0).getDate();
  return {
    from: `${period}-01`,
    to: `${period}-${String(lastDay).padStart(2, "0")}`,
  };
}

const STATUS_LABELS: Record<string, { label: string; className: string }> = {
  draft: { label: "ยังไม่เตรียม", className: "bg-aera-offwhite text-aera-gray" },
  needs_review: { label: "ต้องตรวจ", className: "bg-aera-warmcream text-aera-amber" },
  ready: { label: "พร้อมสร้างไฟล์", className: "bg-aera-greenlight text-aera-green" },
  exporting: { label: "กำลังสร้างไฟล์", className: "bg-aera-warmcream text-aera-amber" },
  // File package ready for the accountant — not “posted to the books”.
  exported: { label: "ไฟล์พร้อม", className: "bg-aera-greenlight text-aera-green" },
  export_failed: { label: "สร้างไฟล์ล้มเหลว", className: "bg-aera-redlight text-aera-red" },
  superseded: { label: "มีแก้ไขหลังสร้างไฟล์", className: "bg-aera-warmcream text-aera-amber" },
};

function StatusBadge({ status, exportVersion }: { status: string; exportVersion?: number }) {
  const meta = STATUS_LABELS[status] ?? {
    label: status,
    className: "bg-aera-offwhite text-aera-gray",
  };
  const label =
    status === "exported" && exportVersion
      ? `${meta.label} v${exportVersion}`
      : meta.label;
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold text-nowrap ${meta.className}`}
    >
      {label}
    </span>
  );
}

type ExportHistoryItem = {
  export_job_id: string;
  period: string;
  adapter_key: string;
  snapshot_version: number;
  status: string;
  requested_at: string | null;
  completed_at: string | null;
  is_latest: boolean;
  retryable?: boolean;
  error_code?: string | null;
  error_message?: string | null;
  provider_reference?: string | null;
  artifacts: { artifact_id: string; filename: string; size_bytes: number }[];
};

type ProviderConfigView = {
  adapter_key: string;
  enabled: boolean;
  mode: string;
  effective_mode?: string;
  configured: boolean;
  has_credentials: boolean;
  credentials_masked: Record<string, string>;
  base_url?: string;
};

type ExportFailureInfo = {
  message: string;
  nextAction: string;
  /** Suggested UI follow-up (no new API — map from error_code). */
  actionKind: "prepare" | "review" | "retry_job" | "retry_create" | "none";
};

/** FE-5 failure UX — map BE error fields to owner-facing cause + next step. */
function describeExportFailure(data: {
  status?: string;
  error_code?: string | null;
  error_message?: string | null;
  retryable?: boolean;
  detail?: unknown;
  error?: unknown;
}): ExportFailureInfo {
  const code = String(data.error_code || "").trim();
  const detail =
    typeof data.detail === "string"
      ? data.detail
      : typeof data.error === "string"
        ? data.error
        : "";
  const rawMsg = String(data.error_message || detail || "").trim();
  const status = String(data.status || "").trim();

  if (code === "validation_failed" || status === "blocked") {
    const needsPrepare = /ไม่มีข้อมูล|prepare|ว่าง/i.test(rawMsg);
    return {
      message: rawMsg || "ยังสร้างไฟล์ไม่ได้ — ตรวจข้อมูลรอบนี้ก่อน",
      nextAction: needsPrepare
        ? "กด「เตรียมรอบ」ก่อน แล้วลองสร้างไฟล์อีกครั้ง"
        : "เปิดตรวจรายการต้องตรวจ/เอกสารขาด แล้วลองสร้างไฟล์อีกครั้ง",
      actionKind: needsPrepare ? "prepare" : "review",
    };
  }
  if (code === "unknown_adapter") {
    return {
      message: rawMsg || "ไม่รองรับรูปแบบการส่งออกนี้",
      nextAction: "ใช้ Excel + ZIP หรือติดต่อผู้ดูแลระบบ",
      actionKind: "none",
    };
  }
  if (code === "missing_snapshot_payload" || code === "not_retryable") {
    return {
      message: rawMsg || "ชุดข้อมูลสำหรับสร้างไฟล์ใช้ต่อไม่ได้",
      nextAction: "กด「สร้างไฟล์」เพื่อสร้างเวอร์ชันใหม่ (ไม่ทับไฟล์เก่า)",
      actionKind: "retry_create",
    };
  }
  if (code === "adapter_error" || status === "failed" || status === "export_failed") {
    const retryable = data.retryable !== false;
    return {
      message: rawMsg || "สร้างไฟล์ล้มเหลว",
      nextAction: retryable
        ? "กด「ลองอีกครั้ง」— ใช้ชุดข้อมูลเดิม ไม่สร้างเวอร์ชันซ้ำ"
        : "ตรวจข้อมูลรอบนี้แล้วสร้างไฟล์เวอร์ชันใหม่",
      actionKind: retryable ? "retry_job" : "prepare",
    };
  }
  return {
    message: rawMsg || "สร้างไฟล์ไม่สำเร็จ",
    nextAction: "รีเฟรชหน้าแล้วลองใหม่ — งานบันทึกไม่ถูกย้อน",
    actionKind: "none",
  };
}

function formatExportFailureToast(info: ExportFailureInfo): string {
  return `${info.message} · ถัดไป: ${info.nextAction}`;
}

/** Next snapshot version for a period — from latest export + history (FE-only, no preview API). */
function nextExportVersion(
  period: string,
  latest: PeriodExportSummary | null | undefined,
  history: ExportHistoryItem[],
): number {
  let maxVer = latest?.snapshot_version ?? 0;
  for (const ex of history) {
    if (ex.period === period && ex.snapshot_version > maxVer) {
      maxVer = ex.snapshot_version;
    }
  }
  return maxVer + 1;
}

function CountLink({
  href,
  children,
  emphasize,
  className = "",
  title = "เปิดรายการของรอบนี้",
}: {
  href: string;
  children: React.ReactNode;
  emphasize?: "amber" | "red";
  className?: string;
  title?: string;
}) {
  const color =
    emphasize === "amber"
      ? "text-aera-amber"
      : emphasize === "red"
        ? "text-aera-red"
        : "text-aera-charcoal";
  return (
    <Link
      href={href}
      className={`tabular-nums underline-offset-2 hover:underline ${color} ${emphasize ? "font-semibold" : ""} ${className}`}
      title={title}
    >
      {children}
    </Link>
  );
}

function AccountingYearSelect({
  open,
  onOpenChange,
  yearLabel,
  yearValue,
  options,
  onSelect,
  className = "",
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  yearLabel: string;
  yearValue: string;
  options: { value: string; label: string }[];
  onSelect: (value: string) => void;
  className?: string;
}) {
  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <AeraButton
          type="button"
          variant="outline"
          size="xs"
          fontWeight="semibold"
          className={`shrink-0 ${className}`}
          aria-label={`เลือกปีบัญชี: ${yearLabel}`}
          aria-expanded={open}
          iconRight={
            <ChevronDown
              size={14}
              className={`text-aera-gray transition-transform duration-200 ${
                open ? "rotate-180" : "rotate-0"
              }`}
            />
          }
        >
          ปี {yearLabel}
        </AeraButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={6}
        className="min-w-[8.5rem] rounded-xl border border-aera-lightgray/80 p-1.5 shadow-[0_10px_30px_rgba(0,0,0,0.08)]"
      >
        {options.map((opt) => {
          const active = yearValue === opt.value;
          return (
            <DropdownMenuItem
              key={opt.value}
              onClick={() => onSelect(opt.value)}
              className="cursor-pointer justify-between gap-3 rounded-lg px-2.5 py-2 text-xs font-semibold text-aera-charcoal"
            >
              <span className="tabular-nums">{opt.label}</span>
              {active ? (
                <Check
                  className="h-3.5 w-3.5 shrink-0 text-aera-amber"
                  strokeWidth={3}
                  aria-hidden
                />
              ) : (
                <span className="h-3.5 w-3.5 shrink-0" aria-hidden />
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Full-width form dropdown — same DropdownMenu stack as year select. */
function AccountingFormSelect({
  open,
  onOpenChange,
  label,
  value,
  options,
  onSelect,
  ariaLabel,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onSelect: (value: string) => void;
  ariaLabel: string;
}) {
  const selected = options.find((o) => o.value === value);
  return (
    <div className="flex flex-col gap-1">
      <p className="text-xs font-semibold text-aera-gray">{label}</p>
      <DropdownMenu open={open} onOpenChange={onOpenChange}>
        <DropdownMenuTrigger asChild>
          <AeraButton
            type="button"
            variant="outline"
            size="sm"
            fontWeight="semibold"
            fullWidth
            className="!justify-between"
            aria-label={ariaLabel}
            aria-expanded={open}
            iconRight={
              <ChevronDown
                size={14}
                className={`text-aera-gray transition-transform duration-200 ${
                  open ? "rotate-180" : "rotate-0"
                }`}
              />
            }
          >
            <span className="min-w-0 flex-1 truncate text-left">
              {selected?.label || "เลือก"}
            </span>
          </AeraButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          sideOffset={6}
          className="z-[10060] min-w-[var(--radix-dropdown-menu-trigger-width)] w-[var(--radix-dropdown-menu-trigger-width)] rounded-xl border border-aera-lightgray/80 p-1.5 shadow-[0_10px_30px_rgba(0,0,0,0.08)]"
        >
          {options.map((opt) => {
            const active = value === opt.value;
            return (
              <DropdownMenuItem
                key={opt.value}
                onClick={() => onSelect(opt.value)}
                className="cursor-pointer justify-between gap-3 rounded-lg px-2.5 py-2 text-xs font-semibold text-aera-charcoal"
              >
                <span className="min-w-0 flex-1 truncate">{opt.label}</span>
                {active ? (
                  <Check
                    className="h-3.5 w-3.5 shrink-0 text-aera-amber"
                    strokeWidth={3}
                    aria-hidden
                  />
                ) : (
                  <span className="h-3.5 w-3.5 shrink-0" aria-hidden />
                )}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export default function AccountingPeriodsShell({
  variant = "page",
  onClose,
  workspaceId: workspaceIdProp,
  serviceId: serviceIdProp,
  ownerUserId: ownerUserIdProp,
  returnTo: returnToProp,
  initialPeriod,
}: AccountingPeriodsShellProps = {}) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const workspaceId =
    workspaceIdProp?.trim() || searchParams.get("workspaceId")?.trim() || "";
  const serviceId =
    serviceIdProp?.trim() || searchParams.get("serviceId")?.trim() || "";
  const returnToRaw =
    returnToProp?.trim() || searchParams.get("returnTo")?.trim() || "";
  const { can, ready: authzReady } = useAuthz(workspaceId);
  const canReadPeriods = can(
    "web.ws.periods",
    "read",
    "หน้า/หน้าต่าง “ส่งบัญชี” (รอบบัญชี) — เปิดจากปุ่มเตรียมส่งบัญชี หรือ /artifacts/accounting",
  );
  const canPreparePeriods = can(
    "web.ws.periods",
    "write",
    "ปุ่มเตรียมรอบบัญชี ในหน้า/หน้าต่างส่งบัญชี",
  );
  const canExportPeriods = can(
    "web.ws.periods",
    "export",
    "ปุ่มสร้างไฟล์ส่งบัญชี / ลองอีกครั้ง / ดาวน์โหลดไฟล์ ในหน้า/หน้าต่างส่งบัญชี",
  );

  const [periods, setPeriods] = useState<AccountingPeriod[] | null>(null);
  const [exports, setExports] = useState<ExportHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyPeriod, setBusyPeriod] = useState<string | null>(null);
  /** Calendar year `YYYY`, or null until first data load. */
  const [selectedYear, setSelectedYear] = useState<string | null>(null);
  const [yearMenuOpen, setYearMenuOpen] = useState(false);
  const [exportYearMenuOpen, setExportYearMenuOpen] = useState(false);
  /** Open accordion months in ประวัติไฟล์ส่งบัญชี. */
  const [openExportPeriods, setOpenExportPeriods] = useState<Set<string>>(
    () => new Set(),
  );
  const [exportProgressOpen, setExportProgressOpen] = useState(false);
  const [exportProgress, setExportProgress] = useState<{
    period: string;
    nextVer: number;
    phase: ExportJobPhase;
    exportJobId?: string;
  } | null>(null);
  /** Keep content mounted while ModalShell plays exit — avoid empty white panel. */
  const [exportPreviewOpen, setExportPreviewOpen] = useState(false);
  const [exportPreview, setExportPreview] = useState<{
    period: string;
    latest?: PeriodExportSummary | null;
    loading: boolean;
    data: ExportPreview | null;
    error: string | null;
  } | null>(null);
  const [selectedAdapterKey, setSelectedAdapterKey] = useState(
    "accountant_excel_v1",
  );
  const [adapterOptions, setAdapterOptions] = useState<ExportAdapterOption[]>([
    {
      adapter_key: "accountant_excel_v1",
      label: "Excel + ZIP",
      kind: "file",
      description: "Workbook 6 sheets + Evidence ZIP",
    },
  ]);
  const [providerConfigOpen, setProviderConfigOpen] = useState(false);
  const [providerConfigs, setProviderConfigs] = useState<ProviderConfigView[]>(
    [],
  );
  const [providerForm, setProviderForm] = useState<{
    adapter_key: string;
    enabled: boolean;
    mode: string;
    connect_id: string;
    user_token: string;
    client_token: string;
    access_token: string;
    base_url: string;
  }>({
    adapter_key: "peak_v1",
    enabled: true,
    mode: "auto",
    connect_id: "",
    user_token: "",
    client_token: "",
    access_token: "",
    base_url: "",
  });
  const [providerSaving, setProviderSaving] = useState(false);
  const [providerSelectOpen, setProviderSelectOpen] = useState(false);
  const [providerModeSelectOpen, setProviderModeSelectOpen] = useState(false);
  const exportPollRef = useRef<number | null>(null);
  const [drawerPeriod, setDrawerPeriod] = useState<string | null>(
    initialPeriod?.trim() || null,
  );
  const [drawerHandoffs, setDrawerHandoffs] = useState<DrawerHandoff[] | null>(
    null,
  );
  const [drawerFeedItems, setDrawerFeedItems] = useState<
    FinancialWorkFeedItem[] | null
  >(null);
  const [drawerFeedRefreshing, setDrawerFeedRefreshing] = useState(false);
  const [drawerIssuesRefreshKey, setDrawerIssuesRefreshKey] = useState(0);
  const drawerHandoffPeriodRef = useRef<string | null>(null);
  const [drawerPreview, setDrawerPreview] = useState<ExportPreview | null>(null);
  const [inspectOpen, setInspectOpen] = useState(false);
  const [inspectClassification, setInspectClassification] =
    useState("needs_review");
  const [inspectScopedHost, setInspectScopedHost] = useState<Element | null>(
    null,
  );

  useLayoutEffect(() => {
    if (!inspectOpen) return;
    setInspectScopedHost(
      document.querySelector("[data-aera-modal-panel]"),
    );
  }, [inspectOpen]);

  const closeInspect = useCallback(() => {
    setInspectOpen(false);
  }, []);

  const openInspect = useCallback((classification = "needs_review") => {
    setInspectClassification(classification);
    setInspectOpen(true);
  }, []);

  const closeExportPreview = useCallback(() => {
    setExportPreviewOpen(false);
  }, []);

  const dismissExportProgress = useCallback(() => {
    setExportProgressOpen(false);
  }, []);

  const clearExportPoll = useCallback(() => {
    if (exportPollRef.current != null) {
      window.clearTimeout(exportPollRef.current);
      exportPollRef.current = null;
    }
  }, []);

  const fetchPeriods = useCallback(async (opts?: { silent?: boolean }) => {
    if (!workspaceId) return;
    const silent = Boolean(opts?.silent);
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const [periodsRes, exportsRes] = await Promise.all([
        marketApiFetch(
          `/api/v1/accounting/periods?workspace_id=${encodeURIComponent(workspaceId)}`,
          { cache: "no-store" },
        ),
        marketApiFetch(
          `/api/v1/accounting/exports?workspace_id=${encodeURIComponent(workspaceId)}`,
          { cache: "no-store" },
        ),
      ]);
      if (!periodsRes.ok) {
        const data = await periodsRes.json().catch(() => ({}));
        throw new Error(
          billlinAuthUserMessage(periodsRes.status, data) ||
            `โหลดรอบบัญชีไม่สำเร็จ (${periodsRes.status})`,
        );
      }
      const data = await periodsRes.json();
      setPeriods(Array.isArray(data.periods) ? data.periods : []);
      if (exportsRes.ok) {
        const ex = await exportsRes.json();
        setExports(Array.isArray(ex.items) ? ex.items : []);
      }
      if (silent) setError(null);
    } catch (err) {
      if (!silent) {
        setError(err instanceof Error ? err.message : "โหลดรอบบัญชีไม่สำเร็จ");
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    void fetchPeriods();
  }, [fetchPeriods]);

  useEffect(() => {
    if (variant !== "drawer" || !periods || periods.length === 0) return;
    const preferred = initialPeriod?.trim() || "";
    setDrawerPeriod((current) => {
      if (current && periods.some((p) => p.period === current)) return current;
      if (preferred && periods.some((p) => p.period === preferred)) return preferred;
      return periods[0]?.period ?? null;
    });
  }, [initialPeriod, periods, variant]);

  const loadDrawerWorkFeed = useCallback(async () => {
    if (variant !== "drawer" || !workspaceId) return;
    const actorId = getUserIdFromAuthToken()?.trim();
    if (!actorId) return;
    const ownerUserId = ownerUserIdProp?.trim() || undefined;
    setDrawerFeedRefreshing(true);
    try {
      const snapshot = await fetchPrepareDrawerWorkFeed({
        workspaceId,
        ownerUserId,
        actorId,
      });
      setDrawerFeedItems(snapshot.items);
    } catch {
      setDrawerFeedItems([]);
    } finally {
      setDrawerFeedRefreshing(false);
    }
  }, [ownerUserIdProp, variant, workspaceId]);

  useEffect(() => {
    if (variant !== "drawer" || !workspaceId) {
      setDrawerFeedItems(null);
      return;
    }
    const cached = readCachedFeedItemsForWorkspace(workspaceId);
    setDrawerFeedItems(cached.length > 0 ? cached : null);
    let cancelled = false;
    const tryLoad = () => {
      if (cancelled) return;
      if (!getUserIdFromAuthToken()?.trim()) return;
      void loadDrawerWorkFeed();
    };
    tryLoad();
    let timer = 0;
    const retry = () => {
      if (cancelled) return;
      if (!getUserIdFromAuthToken()?.trim()) return;
      if (timer) window.clearInterval(timer);
      timer = 0;
      tryLoad();
    };
    const unsub = subscribeAuthSession(retry);
    timer = window.setInterval(retry, 150);
    return () => {
      cancelled = true;
      unsub();
      if (timer) window.clearInterval(timer);
    };
  }, [loadDrawerWorkFeed, variant, workspaceId]);

  const refreshDrawerIssues = useCallback(() => {
    setDrawerHandoffs(null);
    drawerHandoffPeriodRef.current = null;
    setDrawerIssuesRefreshKey((key) => key + 1);
    void loadDrawerWorkFeed();
  }, [loadDrawerWorkFeed]);

  const handleInspectClose = useCallback(() => {
    closeInspect();
    refreshDrawerIssues();
  }, [closeInspect, refreshDrawerIssues]);

  const inspectTitle =
    inspectClassification === "ready"
      ? "รายการพร้อมส่ง"
      : inspectClassification === "missing_evidence"
        ? "เอกสารขาด"
        : "รายการต้องตรวจ";

  const inspectHint =
    inspectClassification === "ready"
      ? "รายการที่พร้อมรวมในไฟล์ส่งบัญชีรอบนี้"
      : inspectClassification === "missing_evidence"
        ? "แนบหรือเปิดหลักฐานให้ครบก่อนสร้างไฟล์"
        : "เลือกคำตอบให้รายการที่ยังต้องตรวจก่อนสร้างไฟล์";

  useEffect(() => {
    if (variant !== "drawer" || !workspaceId || !drawerPeriod) {
      setDrawerHandoffs(null);
      setDrawerPreview(null);
      drawerHandoffPeriodRef.current = null;
      return;
    }
    let cancelled = false;
    const period = drawerPeriod;
    if (drawerHandoffPeriodRef.current !== period) {
      setDrawerHandoffs(null);
      drawerHandoffPeriodRef.current = period;
    }
    const load = async () => {
      try {
        const [handoffsRes, previewRes] = await Promise.all([
          marketApiFetch(
            `/api/v1/accounting/periods/${encodeURIComponent(period)}/handoffs?workspace_id=${encodeURIComponent(workspaceId)}&accounting_status=&limit=200`,
            { cache: "no-store" },
          ),
          marketApiFetch(
            `/api/v1/accounting/periods/${encodeURIComponent(period)}/export-preview?workspace_id=${encodeURIComponent(workspaceId)}&adapter_key=${encodeURIComponent(selectedAdapterKey)}`,
            { method: "POST", cache: "no-store" },
          ),
        ]);
        if (cancelled) return;
        if (handoffsRes.ok) {
          const data = await handoffsRes.json();
          const raw = Array.isArray(data.items) ? data.items : [];
          setDrawerHandoffs(
            raw.filter(
              (item: DrawerHandoff) => item.accounting_status !== "superseded",
            ),
          );
        } else {
          setDrawerHandoffs([]);
        }
        if (previewRes.ok) {
          const data = (await previewRes.json()) as ExportPreview;
          setDrawerPreview(data);
          if (Array.isArray(data.adapters) && data.adapters.length > 0) {
            setAdapterOptions(data.adapters);
          }
        } else {
          setDrawerPreview(null);
        }
      } catch {
        if (!cancelled) {
          setDrawerHandoffs([]);
          setDrawerPreview(null);
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [drawerIssuesRefreshKey, drawerPeriod, periods, selectedAdapterKey, variant, workspaceId]);

  useEffect(() => {
    return () => {
      if (exportPollRef.current != null) {
        window.clearTimeout(exportPollRef.current);
      }
    };
  }, []);

  const availableYears = useMemo(() => {
    const years = new Set<string>();
    for (const p of periods ?? []) {
      const y = p.period?.slice(0, 4);
      if (y && /^\d{4}$/.test(y)) years.add(y);
    }
    for (const ex of exports) {
      const y = ex.period?.slice(0, 4);
      if (y && /^\d{4}$/.test(y)) years.add(y);
    }
    return Array.from(years).sort((a, b) => Number(b) - Number(a));
  }, [exports, periods]);

  // Default to the newest year once; if that year disappears, fall back.
  useEffect(() => {
    if (availableYears.length === 0) return;
    if (selectedYear == null || !availableYears.includes(selectedYear)) {
      setSelectedYear(availableYears[0]!);
    }
  }, [availableYears, selectedYear]);

  const filteredPeriods = useMemo(() => {
    if (!periods || !selectedYear) return [];
    return periods.filter((p) => p.period.startsWith(`${selectedYear}-`));
  }, [periods, selectedYear]);

  const periodTotals = useMemo(() => {
    return filteredPeriods.reduce(
      (acc, p) => {
        acc.recorded += Number(p.recorded_count ?? 0);
        acc.ready += Number(p.ready_count ?? 0);
        acc.needsReview += Number(p.needs_review_count ?? 0);
        acc.missingEvidence += Number(p.missing_evidence_count ?? 0);
        acc.correction += Number(p.correction_count ?? 0);
        return acc;
      },
      {
        recorded: 0,
        ready: 0,
        needsReview: 0,
        missingEvidence: 0,
        correction: 0,
      },
    );
  }, [filteredPeriods]);

  const filteredExports = useMemo(() => {
    if (!selectedYear) return [];
    return exports.filter((ex) => ex.period.startsWith(`${selectedYear}-`));
  }, [exports, selectedYear]);

  /** Group export history by month, newest period + version first. */
  const exportsByPeriod = useMemo(() => {
    const map = new Map<string, ExportHistoryItem[]>();
    for (const ex of filteredExports) {
      const list = map.get(ex.period) ?? [];
      list.push(ex);
      map.set(ex.period, list);
    }
    return Array.from(map.entries())
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([period, items]) => ({
        period,
        items: [...items].sort(
          (a, b) => b.snapshot_version - a.snapshot_version,
        ),
      }));
  }, [filteredExports]);

  const newestExportPeriod = exportsByPeriod[0]?.period ?? null;
  /** Only auto-open newest month once per selected year — never re-open after user collapses. */
  const exportAccordionInitYearRef = useRef<string | null>(null);

  useEffect(() => {
    if (!selectedYear || !newestExportPeriod) {
      if (!selectedYear) exportAccordionInitYearRef.current = null;
      return;
    }

    if (exportAccordionInitYearRef.current !== selectedYear) {
      exportAccordionInitYearRef.current = selectedYear;
      setOpenExportPeriods(new Set([newestExportPeriod]));
      return;
    }

    // After init: only drop months that disappeared (e.g. silent refresh).
    setOpenExportPeriods((prev) => {
      const next = new Set(
        [...prev].filter((p) =>
          exportsByPeriod.some((group) => group.period === p),
        ),
      );
      if (
        next.size === prev.size &&
        [...next].every((p) => prev.has(p))
      ) {
        return prev;
      }
      return next;
    });
  }, [exportsByPeriod, newestExportPeriod, selectedYear]);

  const toggleExportPeriod = useCallback((period: string) => {
    setOpenExportPeriods((prev) => {
      const next = new Set(prev);
      if (next.has(period)) next.delete(period);
      else next.add(period);
      return next;
    });
  }, []);

  const yearValue = selectedYear ?? availableYears[0] ?? "";
  const yearLabel = yearValue ? String(Number(yearValue) + 543) : "—";
  const yearOptions = useMemo(
    () =>
      availableYears.map((y) => ({
        value: y,
        label: String(Number(y) + 543),
      })),
    [availableYears],
  );

  // Refetch when returning to this tab — counts must follow รายรับ/รายจ่าย.
  useEffect(() => {
    if (!workspaceId) return;
    let t: number | null = null;
    const refresh = () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        return;
      }
      // Avoid clobbering in-flight export UI.
      if (busyPeriod || exportProgressOpen) return;
      if (t != null) window.clearTimeout(t);
      t = window.setTimeout(() => {
        void fetchPeriods({ silent: true });
      }, 200);
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      if (t != null) window.clearTimeout(t);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [busyPeriod, exportProgressOpen, fetchPeriods, workspaceId]);

  const tableFallbackHref = useMemo(() => {
    const p = new URLSearchParams();
    if (workspaceId) p.set("workspaceId", workspaceId);
    if (serviceId) p.set("serviceId", serviceId);
    return `/artifacts/table?${p.toString()}`;
  }, [workspaceId, serviceId]);

  /** Prefer origin page (`/` + source=order or `/artifacts/table` + filters). */
  const backHref = useMemo(
    () => sanitizeInternalReturnTo(returnToRaw, tableFallbackHref),
    [returnToRaw, tableFallbackHref],
  );

  const accountingPageHref = useMemo(() => {
    const p = new URLSearchParams();
    if (workspaceId) p.set("workspaceId", workspaceId);
    if (serviceId) p.set("serviceId", serviceId);
    const safeReturn = sanitizeInternalReturnTo(returnToRaw, "");
    if (safeReturn) p.set("returnTo", safeReturn);
    return `/artifacts/accounting?${p.toString()}`;
  }, [returnToRaw, serviceId, workspaceId]);

  const reviewHref = useCallback(
    (period: string, classification = "needs_review") => {
      const p = new URLSearchParams();
      if (workspaceId) p.set("workspaceId", workspaceId);
      if (serviceId) p.set("serviceId", serviceId);
      p.set("period", period);
      p.set("classification", classification);
      // Keep origin so กลับ from review → periods → still returns to the right page.
      const safeReturn = sanitizeInternalReturnTo(returnToRaw, "");
      if (safeReturn) p.set("returnTo", safeReturn);
      return `/artifacts/accounting/review?${p.toString()}`;
    },
    [workspaceId, serviceId, returnToRaw],
  );

  /** Deep-link into รายรับ/รายจ่าย for the same month (+ optional handoff status). */
  const tableHref = useCallback(
    (
      period: string,
      workerId: "bill-lin-income" | "bill-lin-expense",
      accountingStatus?: string,
    ) => {
      const { from, to } = periodDateRange(period);
      const p = new URLSearchParams();
      if (workspaceId) p.set("workspaceId", workspaceId);
      if (serviceId) p.set("serviceId", serviceId);
      p.set("workerId", workerId);
      p.set("filter_from", from);
      p.set("filter_to", to);
      p.set("filter_sort", "record_date");
      p.set("filter_dir", "desc");
      // Same date column as รายรับ/รายจ่าย "วันที่" (= record_date month).
      p.set("filter_date_field", "record_date");
      if (accountingStatus) p.set("filter_accounting_status", accountingStatus);
      p.set("fromAccounting", "1");
      p.set("accountingPeriod", period);
      // So กลับส่งบัญชี → กลับต้นทาง (order home vs table) still works.
      const safeReturn = sanitizeInternalReturnTo(returnToRaw, "");
      if (safeReturn) p.set("accountingReturnTo", safeReturn);
      return `/artifacts/table?${p.toString()}`;
    },
    [workspaceId, serviceId, returnToRaw],
  );

  /** Keep the owner on ข้อมูล when the drawer was opened from there. */
  const drawerTableHref = useCallback(
    (
      period: string,
      workerId: "bill-lin-income" | "bill-lin-expense",
    ) => {
      const { from, to } = periodDateRange(period);
      const base = sanitizeInternalReturnTo(returnToRaw, tableFallbackHref);
      try {
        const url = new URL(base, "https://aera.local");
        url.searchParams.set("workerId", workerId);
        url.searchParams.set("filter_from", from);
        url.searchParams.set("filter_to", to);
        url.searchParams.set("filter_sort", "record_date");
        url.searchParams.set("filter_dir", "desc");
        url.searchParams.set("filter_date_field", "record_date");
        return `${url.pathname}?${url.searchParams.toString()}`;
      } catch {
        return tableHref(period, workerId);
      }
    },
    [returnToRaw, tableFallbackHref, tableHref],
  );

  const handleViewList = useCallback(() => {
    const href = drawerPeriod
      ? drawerTableHref(drawerPeriod, "bill-lin-income")
      : tableFallbackHref;
    router.push(href);
    // Let the filtered list paint behind the drawer before closing.
    window.setTimeout(() => {
      onClose?.();
    }, 220);
  }, [
    drawerPeriod,
    drawerTableHref,
    onClose,
    router,
    tableFallbackHref,
  ]);

  const handlePrepare = async (period: string) => {
    if (!workspaceId) return;
    if (!canPreparePeriods) {
      showToast("บทบาทของคุณเตรียมรอบบัญชีไม่ได้", "error");
      return;
    }
    setBusyPeriod(period);
    try {
      const res = await marketApiFetch(
        `/api/v1/accounting/periods/${encodeURIComponent(period)}/prepare?workspace_id=${encodeURIComponent(workspaceId)}`,
        { method: "POST" },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || data.error || `เตรียมไม่สำเร็จ (${res.status})`);
      const c = data.counts || {};
      showToast(
        `เตรียม ${formatPeriodThai(period)} แล้ว — พร้อม ${c.ready ?? 0} · ต้องตรวจ ${c.needs_review ?? 0} · เอกสารขาด ${c.missing_evidence ?? 0}`,
        "success",
        undefined,
        4500,
      );
      await fetchPeriods();
      if (variant === "drawer") {
        setDrawerIssuesRefreshKey((key) => key + 1);
      }
    } catch (err) {
      showToast(err instanceof Error ? err.message : "เตรียมรอบไม่สำเร็จ", "error");
    } finally {
      setBusyPeriod(null);
    }
  };

  const showExportFailure = (
    period: string,
    data: {
      status?: string;
      error_code?: string | null;
      error_message?: string | null;
      retryable?: boolean;
      detail?: unknown;
      error?: unknown;
    },
  ) => {
    const info = describeExportFailure(data);
    const toastAction =
      info.actionKind === "review"
        ? {
            label: "เปิดตรวจ",
            onClick: () => {
              window.location.href = reviewHref(period);
            },
          }
        : info.actionKind === "prepare"
          ? {
              label: "เตรียมรอบ",
              onClick: () => {
                void handlePrepare(period);
              },
            }
          : undefined;
    showToast(formatExportFailureToast(info), "error", toastAction, 7000);
  };

  const finishExportSuccess = async (period: string, nextVer: number, jobId?: string) => {
    setExportProgressOpen(true);
    setExportProgress({
      period,
      nextVer,
      phase: "succeeded",
      exportJobId: jobId,
    });
    // Hold the final step so users actually see "สร้างไฟล์ ✓" before close.
    await sleep(1400);
    clearExportPoll();
    dismissExportProgress();
    setBusyPeriod(null);
    showToast(
      `สร้างไฟล์ ${formatPeriodThai(period)} v${nextVer} แล้ว — ดาวน์โหลดให้ผู้ทำบัญชีได้ · ยังไม่ได้ลงบัญชี`,
      "success",
      undefined,
      5000,
    );
    await fetchPeriods();
  };

  /** Poll job status in parallel with an in-flight POST (phases commit mid-request). */
  const watchExportJob = async (
    period: string,
    nextVer: number,
    postPromise: Promise<{
      res: Response;
      data: Record<string, unknown>;
    }>,
    knownJobId?: string,
  ) => {
    if (!workspaceId) return;
    const deadline = Date.now() + 180_000;
    let jobId = knownJobId || "";
    const postBox: {
      current: { res: Response; data: Record<string, unknown> } | null;
    } = { current: null };
    let settled = false;

    void postPromise.then((result) => {
      postBox.current = result;
      const id = String(result.data.export_job_id || "");
      if (id) jobId = id;
    });

    const failAndStop = async (payload: {
      status?: string;
      error_code?: string | null;
      error_message?: string | null;
      retryable?: boolean;
      detail?: unknown;
      error?: unknown;
    }) => {
      if (settled) return;
      settled = true;
      clearExportPoll();
      dismissExportProgress();
      setBusyPeriod(null);
      showExportFailure(period, payload);
      await fetchPeriods();
    };

    const succeedAndStop = async (ver: number, id?: string) => {
      if (settled) return;
      settled = true;
      await finishExportSuccess(period, ver, id);
    };

    while (!settled && Date.now() < deadline) {
      if (!jobId) {
        // Discover the job row as soon as enqueue commits (before POST returns).
        try {
          const listRes = await marketApiFetch(
            `/api/v1/accounting/exports?workspace_id=${encodeURIComponent(workspaceId)}&period=${encodeURIComponent(period)}`,
            { cache: "no-store" },
          );
          if (listRes.ok) {
            const listData = await listRes.json().catch(() => ({}));
            const items = Array.isArray(listData.items) ? listData.items : [];
            const match =
              items.find(
                (it: { snapshot_version?: number; status?: string }) =>
                  Number(it.snapshot_version) === nextVer,
              ) ||
              items.find((it: { status?: string }) =>
                ["queued", "validating", "exporting"].includes(
                  String(it.status || ""),
                ),
              );
            if (match?.export_job_id) {
              jobId = String(match.export_job_id);
            }
          }
        } catch {
          /* keep waiting */
        }
      }

      if (jobId) {
        try {
          const res = await marketApiFetch(
            `/api/v1/accounting/exports/${encodeURIComponent(jobId)}?workspace_id=${encodeURIComponent(workspaceId)}`,
            { cache: "no-store" },
          );
          const data = await res.json().catch(() => ({}));
          const job = (
            data && typeof data.job === "object" && data.job
              ? data.job
              : null
          ) as {
            status?: string;
            snapshot_version?: number;
            error_code?: string | null;
            error_message?: string | null;
            retryable?: boolean;
          } | null;
          if (res.ok && job) {
            const status = String(job.status || "");
            const ver = Number(job.snapshot_version) || nextVer;
            if (
              status === "queued" ||
              status === "validating" ||
              status === "exporting"
            ) {
              setExportProgressOpen(true);
              setExportProgress({
                period,
                nextVer: ver,
                phase: phaseFromJobStatus(status),
                exportJobId: jobId,
              });
            } else if (status === "succeeded") {
              await succeedAndStop(ver, jobId);
              return;
            } else if (status === "failed" || status === "blocked") {
              await failAndStop({
                status,
                error_code: job.error_code,
                error_message: job.error_message,
                retryable: job.retryable,
              });
              return;
            }
          }
        } catch {
          /* transient — keep polling */
        }
      }

      const settledPost = postBox.current as {
        res: Response;
        data: Record<string, unknown>;
      } | null;
      if (settledPost) {
        const postStatus = String(settledPost.data.status || "");
        const postVer = Number(settledPost.data.snapshot_version) || nextVer;
        const postJobId = String(settledPost.data.export_job_id || jobId || "");
        if (postStatus === "succeeded") {
          await succeedAndStop(postVer, postJobId || undefined);
          return;
        }
        if (
          postStatus === "failed" ||
          postStatus === "blocked" ||
          postStatus === "not_found" ||
          (!settledPost.res.ok &&
            postStatus !== "queued" &&
            postStatus !== "validating" &&
            postStatus !== "exporting")
        ) {
          await failAndStop({
            status: postStatus || "failed",
            error_code: settledPost.data.error_code as string | null | undefined,
            error_message:
              (settledPost.data.error_message as string | undefined) ||
              (typeof settledPost.data.detail === "string"
                ? settledPost.data.detail
                : undefined) ||
              (typeof settledPost.data.error === "string"
                ? settledPost.data.error
                : undefined),
            retryable: settledPost.data.retryable as boolean | undefined,
            detail: settledPost.data.detail,
            error: settledPost.data.error,
          });
          return;
        }
      }

      await sleep(500);
    }

    if (!settled) {
      await failAndStop({
        status: "failed",
        error_message: "สร้างไฟล์ใช้เวลานานผิดปกติ — รีเฟรชแล้วตรวจประวัติไฟล์",
        retryable: true,
      });
    }
  };

  const openExportPreview = async (
    period: string,
    latest?: PeriodExportSummary | null,
    adapterKey: string = selectedAdapterKey,
  ) => {
    if (!workspaceId) return;
    const resolvedAdapterKey = isAdapterComingSoon(adapterKey)
      ? "accountant_excel_v1"
      : adapterKey;
    setSelectedAdapterKey(resolvedAdapterKey);
    setExportPreview({ period, latest, loading: true, data: null, error: null });
    setExportPreviewOpen(true);
    try {
      const qs = new URLSearchParams({
        workspace_id: workspaceId,
        adapter_key: resolvedAdapterKey,
      });
      const res = await marketApiFetch(
        `/api/v1/accounting/periods/${encodeURIComponent(period)}/export-preview?${qs.toString()}`,
        { method: "POST", cache: "no-store" },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          data.detail ||
            data.error ||
            data.error_message ||
            `โหลดสรุปก่อนส่งไม่สำเร็จ (${res.status})`,
        );
      }
      if (Array.isArray(data.adapters) && data.adapters.length > 0) {
        setAdapterOptions(data.adapters as ExportAdapterOption[]);
      }
      setExportPreview({
        period,
        latest,
        loading: false,
        data: data as ExportPreview,
        error: null,
      });
    } catch (err) {
      setExportPreview({
        period,
        latest,
        loading: false,
        data: null,
        error: err instanceof Error ? err.message : "โหลดสรุปก่อนส่งไม่สำเร็จ",
      });
    }
  };

  const openProviderConfig = async (adapterKey = "peak_v1") => {
    if (!workspaceId) return;
    setProviderConfigOpen(true);
    setProviderForm((f) => ({ ...f, adapter_key: adapterKey }));
    try {
      const res = await marketApiFetch(
        `/api/v1/accounting/providers?workspace_id=${encodeURIComponent(workspaceId)}`,
        { cache: "no-store" },
      );
      const data = await res.json().catch(() => ({}));
      const items = (data.items || []) as ProviderConfigView[];
      setProviderConfigs(items);
      const cur =
        items.find((i) => i.adapter_key === adapterKey) || items[0];
      if (cur) {
        setProviderForm({
          adapter_key: cur.adapter_key,
          enabled: cur.enabled,
          mode: cur.mode || "auto",
          connect_id: "",
          user_token: "",
          client_token: "",
          access_token: "",
          base_url: cur.base_url || "",
        });
      }
    } catch {
      showToast("โหลดการตั้งค่าโปรแกรมบัญชีไม่สำเร็จ", "error");
    }
  };

  const saveProviderConfig = async () => {
    if (!workspaceId) return;
    setProviderSaving(true);
    try {
      const credentials: Record<string, string> = {
        base_url: providerForm.base_url,
      };
      if (providerForm.adapter_key === "peak_v1") {
        credentials.connect_id = providerForm.connect_id;
        credentials.user_token = providerForm.user_token;
        credentials.client_token = providerForm.client_token;
      } else {
        credentials.access_token = providerForm.access_token;
      }
      const res = await marketApiFetch(
        `/api/v1/accounting/providers/${encodeURIComponent(providerForm.adapter_key)}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            workspace_id: workspaceId,
            enabled: providerForm.enabled,
            mode: providerForm.mode,
            credentials,
          }),
        },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.detail || data.error || "บันทึกไม่สำเร็จ");
      }
      showToast(`บันทึกการเชื่อมต่อ ${adapterLabel(providerForm.adapter_key)} แล้ว`, "success");
      setProviderConfigOpen(false);
    } catch (err) {
      showToast(
        err instanceof Error ? err.message : "บันทึกการเชื่อมต่อไม่สำเร็จ",
        "error",
      );
    } finally {
      setProviderSaving(false);
    }
  };

  const handleExport = async (period: string, latest?: PeriodExportSummary | null) => {
    if (!workspaceId) return;
    if (!canExportPeriods) {
      showToast("บทบาทของคุณสร้างไฟล์ส่งบัญชีไม่ได้", "error");
      return;
    }
    const rawAdapterKey =
      exportPreview?.data?.adapter_key ||
      selectedAdapterKey ||
      "accountant_excel_v1";
    if (isAdapterComingSoon(rawAdapterKey)) {
      showToast("FlowAccount เร็วๆนี้ — เลือก Excel + ZIP หรือ CSV + ZIP ก่อน", "error");
      return;
    }
    const adapterKey = rawAdapterKey;
    const previewVer = exportPreview?.data?.snapshot_version;
    const nextVer =
      typeof previewVer === "number" && previewVer > 0
        ? previewVer
        : nextExportVersion(period, latest, exports);
    const operationKey = `export:${workspaceId}:${period}:v${nextVer}:${adapterKey}`;

    closeExportPreview();
    setBusyPeriod(period);
    clearExportPoll();
    setExportProgress({ period, nextVer, phase: "queued" });
    setExportProgressOpen(true);

    const postPromise = marketApiFetch(`/api/v1/accounting/exports`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        workspace_id: workspaceId,
        period,
        adapter_key: adapterKey,
        operation_key: operationKey,
      }),
    }).then(async (res) => ({
      res,
      data: (await res.json().catch(() => ({}))) as Record<string, unknown>,
    }));

    try {
      await watchExportJob(period, nextVer, postPromise);
    } catch (err) {
      clearExportPoll();
      dismissExportProgress();
      setBusyPeriod(null);
      showExportFailure(period, {
        status: "failed",
        error_message: err instanceof Error ? err.message : "สร้างไฟล์ไม่สำเร็จ",
        retryable: true,
      });
    }
  };

  const handleRetryExport = async (period: string, exportJobId: string, nextVer: number) => {
    if (!workspaceId || !exportJobId) return;
    if (!canExportPeriods) {
      showToast("บทบาทของคุณสร้างไฟล์ส่งบัญชีไม่ได้", "error");
      return;
    }
    setBusyPeriod(period);
    clearExportPoll();
    setExportProgress({ period, nextVer, phase: "queued", exportJobId });
    setExportProgressOpen(true);

    const postPromise = marketApiFetch(
      `/api/v1/accounting/exports/${encodeURIComponent(exportJobId)}/retry?workspace_id=${encodeURIComponent(workspaceId)}`,
      { method: "POST" },
    ).then(async (res) => ({
      res,
      data: (await res.json().catch(() => ({}))) as Record<string, unknown>,
    }));

    try {
      await watchExportJob(period, nextVer, postPromise, exportJobId);
    } catch (err) {
      clearExportPoll();
      dismissExportProgress();
      setBusyPeriod(null);
      showExportFailure(period, {
        status: "failed",
        error_message: err instanceof Error ? err.message : "ลองอีกครั้งไม่สำเร็จ",
        retryable: true,
      });
    }
  };

  const handleDownload = useCallback((exportJobId: string, artifactId?: string) => {
    if (!workspaceId || !exportJobId) return;
    if (!canExportPeriods) {
      showToast("บทบาทของคุณดาวน์โหลดไฟล์ส่งบัญชีไม่ได้", "error");
      return;
    }
    const q = new URLSearchParams({ workspace_id: workspaceId });
    if (artifactId) q.set("artifact_id", artifactId);
    const path = `/api/v1/accounting/exports/${encodeURIComponent(exportJobId)}/download?${q.toString()}`;
    void (async () => {
      try {
        const signed = await signBilllinLink(path);
        window.open(signed, "_blank", "noopener,noreferrer");
      } catch (err) {
        showToast(
          err instanceof Error ? err.message : "ขอลิงก์ดาวน์โหลดไม่สำเร็จ",
          "error",
        );
      }
    })();
  }, [canExportPeriods, workspaceId]);

  const drawerPeriodRow = useMemo(
    () => periods?.find((p) => p.period === drawerPeriod) ?? null,
    [drawerPeriod, periods],
  );

  const drawerPeriodOptions = useMemo(
    () =>
      (periods ?? []).map((p) => ({
        value: p.period,
        label: formatPeriodThaiFull(p.period),
      })),
    [periods],
  );

  const drawerIssueGroups: PrepareIssueGroup[] = useMemo(() => {
    const feedItems = filterFeedItemsForPeriod(
      drawerFeedItems ?? [],
      drawerPeriod,
    );
    const handoffs = drawerHandoffs ?? [];
    const row = drawerPeriodRow;
    const summary = drawerPreview?.period_summary;
    const periodFeedCounts = countFeedItemsByBucket(feedItems);
    const workHrefBase = {
      serviceId,
      workspaceId,
      returnTo: sanitizeInternalReturnTo(returnToRaw, ""),
    };

    const toFeedItems = (bucket: "needs_action" | "waiting") =>
      feedItems
        .filter((item) => item.bucket === bucket)
        .map((item) =>
          feedItemToPrepareIssue(
            item,
            buildPrepareWorkItemHref({ ...workHrefBase, item }),
          ),
        );

    const needsActionItems = [
      ...toFeedItems("needs_action"),
      ...handoffs
        .filter(
          (item) =>
            item.classification_status === "needs_review" ||
            item.classification_status === "missing_evidence",
        )
        .map((item) => ({
          id: `handoff:${item.handoff_record_id}`,
          title: handoffIssueTitle(item),
          subtitle: handoffPrepareSubtitle(item),
          amountLabel: formatHandoffAmount(item.amount),
          href: drawerPeriod
            ? reviewHref(drawerPeriod, item.classification_status)
            : accountingPageHref,
        })),
    ];
    const waitingItems = toFeedItems("waiting");
    const handoffNeedsReview =
      summary?.needs_review_count ?? row?.needs_review_count ?? 0;
    const handoffMissingEvidence =
      summary?.missing_evidence_count ?? row?.missing_evidence_count ?? 0;

    // Same definition as /artifacts periods 「แก้ไขหลังส่ง」 column.
    const periodHasSuccessfulExport =
      row?.latest_export?.status === "succeeded" ||
      Boolean(
        drawerPeriod &&
          exports.some(
            (ex) => ex.period === drawerPeriod && ex.status === "succeeded",
          ),
      );
    const corrections = periodCorrectionHandoffs(
      handoffs,
      periodHasSuccessfulExport,
    );
    const correctionItems = corrections.map((item) => ({
      id: `handoff:${item.handoff_record_id}`,
      title: handoffIssueTitle(item),
      subtitle: handoffPrepareSubtitle(item),
      amountLabel: formatHandoffAmount(item.amount),
      href: drawerPeriod
        ? reviewHref(drawerPeriod, "ready")
        : accountingPageHref,
    }));
    const correctionCount = Math.max(
      Number(row?.correction_count ?? 0),
      correctionItems.length,
    );

    return [
      {
        key: "needs_action",
        title: "ต้องให้คุณทำ",
        count: Math.max(
          periodFeedCounts.needs_action + handoffNeedsReview + handoffMissingEvidence,
          needsActionItems.length,
        ),
        emptyLabel: "ไม่มีรายการ",
        items: needsActionItems,
      },
      {
        key: "waiting",
        title: "Bill-Lin กำลังถือ",
        count: Math.max(periodFeedCounts.waiting, waitingItems.length),
        emptyLabel: "ไม่มีรายการ",
        items: waitingItems,
      },
      {
        key: "correction",
        title: "แก้ไขหลังส่ง",
        count: correctionCount,
        emptyLabel: "ไม่มีรายการ",
        items: correctionItems,
      },
    ];
  }, [
    accountingPageHref,
    drawerFeedItems,
    drawerHandoffs,
    drawerPeriod,
    drawerPeriodRow,
    drawerPreview,
    exports,
    returnToRaw,
    reviewHref,
    serviceId,
    workspaceId,
  ]);

  const drawerExportRows: PrepareExportRow[] = useMemo(() => {
    if (!drawerPeriod) return [];
    return exports
      .filter((ex) => ex.period === drawerPeriod)
      .sort((a, b) => b.snapshot_version - a.snapshot_version)
      .map((ex) => {
        const artifacts =
          ex.artifacts?.length
            ? ex.artifacts
            : [{ artifact_id: "", filename: "", size_bytes: 0 }];
        return {
          id: ex.export_job_id,
          version: ex.snapshot_version,
          whenLabel: formatExportWhen(ex.completed_at || ex.requested_at),
          statusLabel:
            ex.status === "succeeded"
              ? "ไฟล์พร้อม"
              : ex.status === "failed" || ex.status === "blocked"
                ? "สร้างไฟล์ล้มเหลว"
                : "กำลังสร้างไฟล์",
          isLatest: Boolean(ex.is_latest),
          failed: ex.status === "failed" || ex.status === "blocked",
          canDownload: ex.status === "succeeded",
          downloads:
            ex.status === "succeeded"
              ? artifacts.map((artifact) => ({
                  id: artifact.artifact_id || "primary",
                  label: artifact.filename.includes("Evidence")
                    ? "หลักฐาน"
                    : primaryDownloadLabel(ex.adapter_key, artifact.filename),
                  onClick: () =>
                    handleDownload(
                      ex.export_job_id,
                      artifact.artifact_id || undefined,
                    ),
                }))
              : [],
        };
      });
  }, [drawerPeriod, exports, handleDownload]);

  // Accounting readiness / handoff columns — prefer periods table counts so
  // เตรียมส่งบัญชี matches /artifacts/accounting (export-preview counts
  // correction marker rows and can include already-exported ready items).
  const accountingSummary = drawerPreview?.period_summary;
  const drawerReadyCount =
    drawerPeriodRow?.ready_count ?? accountingSummary?.ready_count ?? 0;
  const drawerHandoffNeedsReview =
    drawerPeriodRow?.needs_review_count ??
    accountingSummary?.needs_review_count ??
    0;
  const drawerHandoffMissing =
    drawerPeriodRow?.missing_evidence_count ??
    accountingSummary?.missing_evidence_count ??
    0;
  const drawerCorrection =
    drawerPeriodRow?.correction_count ??
    accountingSummary?.correction_count ??
    0;
  const periodFeedCounts = useMemo(
    () =>
      countFeedItemsByBucket(
        filterFeedItemsForPeriod(drawerFeedItems ?? [], drawerPeriod),
      ),
    [drawerFeedItems, drawerPeriod],
  );
  // ต้องให้คุณทำ / Bill-Lin กำลังถือ stay on work-queue feed (+ accounting review/missing).
  const drawerNeedsAction =
    periodFeedCounts.needs_action + drawerHandoffNeedsReview + drawerHandoffMissing;
  const drawerWaiting = periodFeedCounts.waiting;
  // ความพร้อมรอบนี้ from accounting columns only (พร้อมส่ง ÷ พร้อมส่ง+ต้องตรวจ+เอกสารขาด+แก้ไขหลังส่ง).
  const drawerReadinessDenom =
    drawerReadyCount +
    drawerHandoffNeedsReview +
    drawerHandoffMissing +
    drawerCorrection;
  const drawerReadinessPercent =
    drawerReadinessDenom > 0
      ? Math.round((drawerReadyCount / drawerReadinessDenom) * 100)
      : 0;
  const drawerNextVersion = drawerPeriod
    ? nextExportVersion(drawerPeriod, drawerPeriodRow?.latest_export, exports)
    : 1;
  const drawerBusy =
    Boolean(drawerPeriod) &&
    (busyPeriod === drawerPeriod ||
      (exportProgressOpen && exportProgress?.period === drawerPeriod));
  const drawerCanCreate = Boolean(drawerPeriod) && !drawerBusy;
  const drawerCreateReason = drawerPreview && !drawerPreview.ready_to_export
    ? "ยังสร้างไม่ได้ — เปิดสรุปก่อนสร้างเพื่อดูรายละเอียด"
    : undefined;
  const drawerStatusMeta =
    STATUS_LABELS[drawerPeriodRow?.period_status || ""] ?? {
      label: drawerPeriodRow?.period_status || "—",
      className: "",
    };

  const openDrawerExportPreview = () => {
    if (!drawerPeriod) return;
    void openExportPreview(drawerPeriod, drawerPeriodRow?.latest_export);
  };

  if (authzReady && !canReadPeriods) {
    return (
      <div
        className="flex min-h-0 flex-1 flex-col gap-3 bg-aera-white px-5 py-8 text-sm text-aera-gray"
        data-testid="accounting-no-role-access"
      >
        <p>บทบาทของคุณยังไม่มีสิทธิ์ดูรอบบัญชีของร้านนี้</p>
        {onClose ? (
          <AeraButton type="button" variant="secondary" size="sm" onClick={onClose}>
            ปิด
          </AeraButton>
        ) : null}
      </div>
    );
  }

  return (
    <EvidenceAccessProvider workspaceId={workspaceId}>
    <div
      className={
        variant === "drawer"
          ? "flex h-full min-h-0 flex-col bg-aera-white text-aera-charcoal [&_a]:cursor-pointer [&_button:not(:disabled)]:cursor-pointer [&_button:disabled]:cursor-not-allowed"
          : "flex h-dvh flex-col bg-aera-offwhite text-aera-charcoal [&_a]:cursor-pointer [&_button:not(:disabled)]:cursor-pointer [&_button:disabled]:cursor-not-allowed"
      }
    >
      {variant === "page" ? (
      <div className="flex items-center gap-3 border-b border-aera-lightgray bg-aera-white p-3 md:p-4">
        <AeraButton
          href={backHref}
          variant="ghost"
          size="sm"
          paddingSize="none"
          className="flex! h-9! w-9! shrink-0 items-center justify-center rounded-xl! bg-aera-offwhite text-aera-gray hover:text-aera-charcoal"
          aria-label="กลับหน้าบันทึก"
          icon={<ArrowLeft size={18} />}
        />
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-aera-greenlight">
          <FileSpreadsheet className="h-5 w-5 text-aera-green" />
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">ส่งบัญชี</p>
          <p className="truncate text-xs text-aera-gray">
            เตรียม ตรวจ และสร้างไฟล์จากข้อมูล AERA — ไม่ต้องเชื่อม Google ·
            การสร้างไฟล์ไม่ใช่การลงบัญชีเสร็จ
          </p>
        </div>
        <div className="ml-auto flex shrink-0 items-center gap-1.5">
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <AeraButton
                    type="button"
                    variant="outline"
                    size="xs"
                    fontWeight="semibold"
                    disabled
                    onClick={() => void openProviderConfig("peak_v1")}
                    icon={<Settings2 size={14} />}
                  >
                    โปรแกรมบัญชี
                  </AeraButton>
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom" align="end">
                เร็วๆ นี้ — ยังไม่เปิดใช้การเชื่อมต่อโปรแกรมบัญชี
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <AeraButton
            type="button"
            variant="outline"
            size="xs"
            fontWeight="semibold"
            onClick={() => void fetchPeriods()}
            disabled={loading}
            icon={
              <RefreshCw
                size={14}
                className={loading ? "animate-spin" : undefined}
              />
            }
          >
            รีเฟรช
          </AeraButton>
        </div>
      </div>
      ) : null}

      <ModalShell
        isOpen={exportProgressOpen}
        onClose={() => {
          /* Closes only when export finishes (exportProgress dismissed). */
        }}
        onExited={() => setExportProgress(null)}
        align="center"
        preventBackdropClose
        zIndexClass="z-[10050]"
        // ModalShell defaults to w-full — force a small centered card.
        panelClassName="!mx-auto !w-[min(360px,calc(100vw-2.5rem))] max-w-[360px] rounded-2xl border border-aera-lightgray bg-aera-white p-4 shadow-xl"
      >
        {exportProgress ? (
          <div
            role="status"
            aria-live="polite"
            aria-busy={exportProgress.phase !== "succeeded"}
            className="flex flex-col items-center text-center"
          >
            <div
              className={`flex h-9 w-9 items-center justify-center rounded-full ${
                exportProgress.phase === "succeeded"
                  ? "bg-aera-greenlight"
                  : "bg-aera-warmcream"
              }`}
            >
              {exportProgress.phase === "succeeded" ? (
                <Check className="h-4 w-4 text-aera-green" strokeWidth={3} />
              ) : (
                <Loader2 className="h-4 w-4 animate-spin text-aera-amber" />
              )}
            </div>
            <p className="mt-3 text-sm font-semibold text-aera-charcoal">
              {exportProgress.phase === "succeeded"
                ? "สร้างชุดส่งบัญชีแล้ว"
                : "กำลังสร้างชุดส่งบัญชี"}
            </p>
            <p className="mt-0.5 text-xs font-semibold text-aera-gray">
              {formatPeriodThai(exportProgress.period)} · v{exportProgress.nextVer}
            </p>
            <ol className="mt-3 flex w-full flex-col gap-1.5 text-left text-[11px]">
              {EXPORT_PHASE_STEPS.map((step, idx) => {
                const current = EXPORT_PHASE_INDEX[exportProgress.phase];
                const allDone = exportProgress.phase === "succeeded";
                const done = allDone || idx < current;
                const active = !allDone && idx === current;
                return (
                  <li
                    key={step.key}
                    className={`flex items-start gap-2 rounded-lg px-2.5 py-1.5 ${
                      active
                        ? "bg-aera-warmcream"
                        : done
                          ? "bg-aera-greenlight/60"
                          : "bg-aera-offwhite"
                    }`}
                  >
                    <span
                      className={`mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full ${
                        active
                          ? "bg-aera-amber text-white"
                          : done
                            ? "bg-aera-green text-white"
                            : "bg-aera-lightgray text-[10px] font-bold text-aera-gray"
                      }`}
                      aria-hidden
                    >
                      {done ? (
                        <Check className="h-2.5 w-2.5" strokeWidth={3} />
                      ) : active ? (
                        <Loader2 className="h-2.5 w-2.5 animate-spin" strokeWidth={3} />
                      ) : (
                        idx + 1
                      )}
                    </span>
                    <span className="min-w-0">
                      <span
                        className={
                          active
                            ? "font-semibold text-aera-amber"
                            : done
                              ? "font-semibold text-aera-green"
                              : "font-medium text-aera-gray"
                        }
                      >
                        {step.label}
                        {active ? "…" : ""}
                      </span>
                      {active ? (
                        <span className="mt-0.5 block font-medium leading-snug text-aera-charcoal/80">
                          {step.detail}
                        </span>
                      ) : null}
                    </span>
                  </li>
                );
              })}
            </ol>
            <p className="mt-3 text-[11px] font-medium leading-snug text-aera-gray">
              {exportProgress.phase === "succeeded"
                ? "ไฟล์พร้อมดาวน์โหลด · ยังไม่ได้ลงบัญชี"
                : "สร้างไฟล์ให้ผู้ทำบัญชี · ยังไม่ได้ลงบัญชี · ปิดเองเมื่อเสร็จ"}
            </p>
          </div>
        ) : null}
      </ModalShell>

      <ModalShell
        isOpen={exportPreviewOpen}
        onClose={closeExportPreview}
        onExited={() => setExportPreview(null)}
        align="center"
        zIndexClass="z-[10040]"
        panelClassName="!mx-auto !w-[min(420px,calc(100vw-2rem))] max-w-[420px] rounded-2xl border border-aera-lightgray bg-aera-white p-4 shadow-xl [&_a]:cursor-pointer [&_button:not(:disabled)]:cursor-pointer [&_button:disabled]:cursor-not-allowed"
      >
        {exportPreview ? (
          <div className="flex flex-col gap-3">
            <div>
              <p className="text-sm font-semibold text-aera-charcoal">
                สรุปก่อนสร้างไฟล์
              </p>
              <p className="mt-0.5 text-xs text-aera-gray">
                {formatPeriodThai(exportPreview.period)}
                {exportPreview.data
                  ? ` · เวอร์ชัน v${exportPreview.data.snapshot_version}`
                  : ""}
                {" · "}สร้างไฟล์ ≠ ลงบัญชีเสร็จ
              </p>
            </div>
            {exportPreview.loading ? (
              <div className="flex items-center justify-center gap-2 py-8 text-sm text-aera-gray">
                <Loader2 className="h-4 w-4 animate-spin text-aera-amber" />
                กำลังสรุปข้อมูลรอบนี้…
              </div>
            ) : exportPreview.error ? (
              <p className="text-sm text-aera-red">{exportPreview.error}</p>
            ) : exportPreview.data ? (
              <>
                <div>
                  <div className="mb-1.5 flex items-center justify-between gap-2">
                    <p className="text-[11px] font-semibold text-aera-gray">
                      ปลายทางส่งบัญชี
                    </p>
                    {(exportPreview.data.adapter_kind === "provider" ||
                      (exportPreview.data.adapters || []).some(
                        (a) => a.kind === "provider",
                      )) && (
                      <AeraButton
                        type="button"
                        variant="link"
                        size="xxs"
                        fontWeight="semibold"
                        textColor="amber"
                        className="h-auto! p-0! text-[10px]! underline-offset-2 hover:underline"
                        onClick={() =>
                          void openProviderConfig(
                            exportPreview.data?.adapter_kind === "provider"
                              ? exportPreview.data.adapter_key || "peak_v1"
                              : "peak_v1",
                          )
                        }
                      >
                        ตั้งค่าการเชื่อมต่อ
                      </AeraButton>
                    )}
                  </div>
                  <div className="flex max-h-40 flex-col gap-1.5 overflow-y-auto">
                    {(exportPreview.data.adapters?.length
                      ? exportPreview.data.adapters
                      : adapterOptions
                    ).map((opt) => {
                      const comingSoon = isAdapterComingSoon(opt.adapter_key);
                      const selected =
                        !comingSoon &&
                        (exportPreview.data?.adapter_key ||
                          selectedAdapterKey) === opt.adapter_key;
                      return (
                        <label
                          key={opt.adapter_key}
                          aria-disabled={comingSoon}
                          className={`flex items-start gap-2 rounded-lg border px-2.5 py-2 text-xs ${
                            comingSoon
                              ? "cursor-not-allowed border-aera-lightgray bg-aera-offwhite/70 opacity-60"
                              : selected
                                ? "cursor-pointer border-aera-amber bg-aera-warmcream/60"
                                : "cursor-pointer border-aera-lightgray hover:bg-aera-offwhite"
                          }`}
                        >
                          <input
                            type="radio"
                            name="export-adapter"
                            className="mt-0.5"
                            checked={selected}
                            disabled={comingSoon}
                            onChange={() => {
                              if (comingSoon) return;
                              void openExportPreview(
                                exportPreview.period,
                                exportPreview.latest,
                                opt.adapter_key,
                              );
                            }}
                          />
                          <span>
                            <span className="font-semibold text-aera-charcoal">
                              {opt.label}
                            </span>
                            {comingSoon ? (
                              <span className="ml-1 rounded bg-aera-amber/15 px-1.5 py-0.5 text-[10px] font-semibold text-aera-amberdark">
                                เร็วๆนี้
                              </span>
                            ) : opt.kind === "provider" ? (
                              <span className="ml-1 rounded bg-aera-offwhite px-1.5 py-0.5 text-[10px] text-aera-gray">
                                provider
                              </span>
                            ) : null}
                            {comingSoon ? (
                              <span className="mt-0.5 block text-[10px] text-aera-gray">
                                ยังไม่พร้อมให้เลือก — ใช้ Excel + ZIP หรือ CSV + ZIP ก่อน
                              </span>
                            ) : opt.description ? (
                              <span className="mt-0.5 block text-[10px] text-aera-gray">
                                {opt.description}
                              </span>
                            ) : null}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
                {exportPreview.data.period_summary ? (
                  <div className="grid grid-cols-2 gap-1.5 text-[11px]">
                    <div className="rounded-lg bg-aera-offwhite px-2.5 py-1.5">
                      พร้อมส่ง{" "}
                      <span className="font-semibold tabular-nums">
                        {exportPreview.data.period_summary.ready_count}
                      </span>
                    </div>
                    <div className="rounded-lg bg-aera-offwhite px-2.5 py-1.5">
                      ต้องตรวจ{" "}
                      <span className="font-semibold tabular-nums">
                        {exportPreview.data.period_summary.needs_review_count}
                      </span>
                    </div>
                    <div className="rounded-lg bg-aera-offwhite px-2.5 py-1.5">
                      เอกสารขาด{" "}
                      <span className="font-semibold tabular-nums">
                        {
                          exportPreview.data.period_summary
                            .missing_evidence_count
                        }
                      </span>
                    </div>
                    <div className="rounded-lg bg-aera-offwhite px-2.5 py-1.5">
                      แก้ไขหลังส่ง{" "}
                      <span className="font-semibold tabular-nums">
                        {exportPreview.data.period_summary.correction_count}
                      </span>
                    </div>
                  </div>
                ) : null}
                <table className="w-full text-sm">
                  <tbody>
                    {SHEET_COUNT_ROWS.map((row) => (
                      <tr
                        key={row.key}
                        className="border-b border-aera-lightgray/70 last:border-b-0"
                      >
                        <td className="py-1.5 text-aera-charcoal">{row.label}</td>
                        <td className="py-1.5 text-right font-semibold tabular-nums">
                          {Number(
                            exportPreview.data!.sheet_counts[row.key] ?? 0,
                          ).toLocaleString("th-TH")}{" "}
                          รายการ
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(() => {
                  const warnings = (exportPreview.data.issues || []).filter(
                    (i) => i.severity === "warning",
                  );
                  const errors = (exportPreview.data.issues || []).filter(
                    (i) => i.severity === "error",
                  );
                  return (
                    <div className="space-y-1.5 text-[11px]">
                      {errors.length > 0 ? (
                        <div className="rounded-lg bg-aera-redlight px-2.5 py-2 text-aera-red">
                          {errors.map((e, i) => (
                            <p key={`e-${i}`} className="font-semibold">
                              {e.message}
                            </p>
                          ))}
                        </div>
                      ) : null}
                      {warnings.length > 0 ? (
                        <div className="rounded-lg bg-aera-warmcream px-2.5 py-2 text-aera-amber">
                          <p className="font-semibold">
                            คำเตือน {warnings.length} ข้อ — ไม่บล็อกการสร้างไฟล์
                          </p>
                          <ul className="mt-1 list-disc space-y-0.5 pl-4">
                            {warnings.slice(0, 4).map((w, i) => (
                              <li key={`w-${i}`}>{w.message}</li>
                            ))}
                            {warnings.length > 4 ? (
                              <li>และอีก {warnings.length - 4} ข้อ</li>
                            ) : null}
                          </ul>
                        </div>
                      ) : null}
                    </div>
                  );
                })()}
                <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
                  {(exportPreview.data.period_summary?.needs_review_count ||
                    0) > 0 ||
                  (exportPreview.data.period_summary?.missing_evidence_count ||
                    0) > 0 ? (
                    <AeraButton
                      href={reviewHref(
                        exportPreview.period,
                        (exportPreview.data.period_summary
                          ?.needs_review_count || 0) > 0
                          ? "needs_review"
                          : "missing_evidence",
                      )}
                      variant="outline"
                      size="xs"
                      fontWeight="semibold"
                      onClick={closeExportPreview}
                    >
                      เปิดรายการต้องตรวจ
                    </AeraButton>
                  ) : null}
                  <AeraButton
                    type="button"
                    variant="outline"
                    size="xs"
                    fontWeight="semibold"
                    onClick={closeExportPreview}
                  >
                    ยกเลิก
                  </AeraButton>
                  <AeraButton
                    type="button"
                    variant="secondary"
                    size="xs"
                    fontWeight="semibold"
                    disabled={!exportPreview.data.ready_to_export}
                    onClick={() =>
                      void handleExport(
                        exportPreview.period,
                        exportPreview.latest,
                      )
                    }
                    title={
                      exportPreview.data.ready_to_export
                        ? `สร้างด้วย ${adapterLabel(exportPreview.data.adapter_key || selectedAdapterKey)}`
                        : "ยังสร้างไม่ได้ — แก้ข้อผิดพลาดก่อน"
                    }
                    className="border-aera-green! bg-aera-green! text-aera-white! hover:opacity-90"
                  >
                    สร้างชุดส่งบัญชี v{exportPreview.data.snapshot_version}
                  </AeraButton>
                </div>
              </>
            ) : null}
          </div>
        ) : null}
      </ModalShell>

      <ModalShell
        isOpen={providerConfigOpen}
        onClose={() => {
          setProviderConfigOpen(false);
          setProviderSelectOpen(false);
          setProviderModeSelectOpen(false);
        }}
        align="center"
        zIndexClass="z-[10050]"
        panelClassName="!mx-auto !w-[min(440px,calc(100vw-2rem))] max-w-[440px] rounded-2xl border border-aera-lightgray bg-aera-white p-4 shadow-xl [&_button:not(:disabled)]:cursor-pointer"
      >
        <div className="flex flex-col gap-3">
          <div>
            <p className="text-sm font-semibold text-aera-charcoal">
              เชื่อมต่อโปรแกรมบัญชี
            </p>
            <p className="mt-0.5 text-xs text-aera-gray">
              ใส่รหัสจาก PEAK หรือ FlowAccount เพื่อส่งข้อมูลจาก AERA ไปยังโปรแกรมบัญชีของคุณ
            </p>
          </div>

          <AccountingFormSelect
            open={providerSelectOpen}
            onOpenChange={setProviderSelectOpen}
            label="โปรแกรมบัญชี"
            value={providerForm.adapter_key}
            ariaLabel="เลือกโปรแกรมบัญชี"
            options={(providerConfigs.length
              ? providerConfigs
              : [
                  { adapter_key: "peak_v1" },
                  { adapter_key: "flowaccount_v1" },
                  { adapter_key: "provider_sim_v1" },
                ]
            ).map((c) => ({
              value: c.adapter_key,
              label: adapterLabel(c.adapter_key),
            }))}
            onSelect={(value) => {
              setProviderSelectOpen(false);
              void openProviderConfig(value);
            }}
          />

          <div className="flex items-center justify-between gap-3 rounded-xl border border-aera-lightgray bg-aera-offwhite/60 px-3 py-2.5">
            <div className="min-w-0">
              <p className="text-xs font-semibold text-aera-charcoal">
                ใช้ส่งข้อมูลไป {adapterLabel(providerForm.adapter_key)}
              </p>
              <p className="mt-0.5 text-[11px] text-aera-gray">
                {providerForm.enabled
                  ? "เปิดอยู่ — เลือกปลายทางนี้ได้ตอนสร้างชุดส่งบัญชี"
                  : "ปิดอยู่ — จะไม่แสดงเป็นตัวเลือกตอนส่งบัญชี"}
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={providerForm.enabled}
              aria-label={
                providerForm.enabled
                  ? `ปิดการส่งไป ${adapterLabel(providerForm.adapter_key)}`
                  : `เปิดการส่งไป ${adapterLabel(providerForm.adapter_key)}`
              }
              onClick={() =>
                setProviderForm((f) => ({ ...f, enabled: !f.enabled }))
              }
              className={`relative h-6 w-11 shrink-0 rounded-full transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aera-amber/40 ${
                providerForm.enabled ? "bg-aera-green" : "bg-aera-lightgray"
              }`}
            >
              <span
                aria-hidden
                className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-aera-white shadow-sm transition-transform duration-200 ${
                  providerForm.enabled ? "translate-x-5" : "translate-x-0"
                }`}
              />
            </button>
          </div>

          <AccountingFormSelect
            open={providerModeSelectOpen}
            onOpenChange={setProviderModeSelectOpen}
            label="วิธีส่ง"
            value={providerForm.mode}
            ariaLabel="เลือกวิธีส่ง"
            options={PROVIDER_MODE_OPTIONS.map((o) => ({
              value: o.value,
              label: o.label,
            }))}
            onSelect={(value) => {
              setProviderForm((f) => ({ ...f, mode: value }));
              setProviderModeSelectOpen(false);
            }}
          />
          <p className="-mt-1 text-[11px] text-aera-gray">
            {providerForm.mode === "sim"
              ? "ระบบจะทดลองสร้างใบตอบรับใน AERA โดยยังไม่ส่งไปโปรแกรมบัญชีจริง"
              : providerForm.mode === "live"
                ? "ระบบจะส่งไปโปรแกรมบัญชีทันทีเมื่อสร้างชุดส่งบัญชี"
                : "ถ้าใส่รหัสครบแล้วจะส่งจริง ถ้ายังไม่มีจะทดลองส่งแทน"}
          </p>

          <label className="flex flex-col gap-1 text-xs font-semibold text-aera-gray">
            ที่อยู่เซิร์ฟเวอร์
            <input
              className="rounded-lg border border-aera-lightgray px-2.5 py-2 text-sm font-normal text-aera-charcoal"
              value={providerForm.base_url}
              onChange={(e) =>
                setProviderForm((f) => ({ ...f, base_url: e.target.value }))
              }
              placeholder="เว้นว่างเพื่อใช้ค่ามาตรฐาน"
            />
            <span className="font-normal text-[11px] text-aera-gray">
              ส่วนใหญ่ไม่ต้องใส่ ยกเว้นทีมเทคนิคกำหนดไว้
            </span>
          </label>

          {providerForm.adapter_key === "peak_v1" ? (
            <>
              <label className="flex flex-col gap-1 text-xs font-semibold text-aera-gray">
                รหัสเชื่อมต่อ
                <input
                  className="rounded-lg border border-aera-lightgray px-2.5 py-2 text-sm font-normal text-aera-charcoal"
                  value={providerForm.connect_id}
                  onChange={(e) =>
                    setProviderForm((f) => ({
                      ...f,
                      connect_id: e.target.value,
                    }))
                  }
                  placeholder="จากหน้าตั้งค่า API ของ PEAK"
                  autoComplete="off"
                />
              </label>
              <label className="flex flex-col gap-1 text-xs font-semibold text-aera-gray">
                โทเค็นผู้ใช้
                <input
                  className="rounded-lg border border-aera-lightgray px-2.5 py-2 text-sm font-normal text-aera-charcoal"
                  value={providerForm.user_token}
                  onChange={(e) =>
                    setProviderForm((f) => ({
                      ...f,
                      user_token: e.target.value,
                    }))
                  }
                  placeholder="เว้นว่างหากไม่ต้องการเปลี่ยนรหัสเดิม"
                  autoComplete="off"
                />
              </label>
              <label className="flex flex-col gap-1 text-xs font-semibold text-aera-gray">
                โทเค็นไคลเอนต์
                <input
                  className="rounded-lg border border-aera-lightgray px-2.5 py-2 text-sm font-normal text-aera-charcoal"
                  value={providerForm.client_token}
                  onChange={(e) =>
                    setProviderForm((f) => ({
                      ...f,
                      client_token: e.target.value,
                    }))
                  }
                  placeholder="เว้นว่างหากไม่ต้องการเปลี่ยนรหัสเดิม"
                  autoComplete="off"
                />
              </label>
            </>
          ) : providerForm.adapter_key === "provider_sim_v1" ? (
            <p className="rounded-lg bg-aera-offwhite px-2.5 py-2 text-[11px] text-aera-gray">
              โหมดทดลองไม่ต้องใส่รหัส — ใช้ตรวจว่าระบบสร้างใบตอบรับได้โดยยังไม่ส่งออกจริง
            </p>
          ) : (
            <label className="flex flex-col gap-1 text-xs font-semibold text-aera-gray">
              รหัสเข้าถึง
              <input
                className="rounded-lg border border-aera-lightgray px-2.5 py-2 text-sm font-normal text-aera-charcoal"
                value={providerForm.access_token}
                onChange={(e) =>
                  setProviderForm((f) => ({
                    ...f,
                    access_token: e.target.value,
                  }))
                }
                placeholder="จากหน้าตั้งค่า API ของ FlowAccount"
                autoComplete="off"
              />
            </label>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <AeraButton
              type="button"
              variant="outline"
              size="sm"
              fontWeight="semibold"
              onClick={() => setProviderConfigOpen(false)}
            >
              ยกเลิก
            </AeraButton>
            <AeraButton
              type="button"
              variant="primary"
              size="sm"
              fontWeight="semibold"
              disabled={providerSaving}
              onClick={() => void saveProviderConfig()}
            >
              {providerSaving ? "กำลังบันทึก…" : "บันทึก"}
            </AeraButton>
          </div>
        </div>
      </ModalShell>

      <ModalShell
        isOpen={inspectOpen}
        onClose={handleInspectClose}
        onExited={() => setInspectScopedHost(null)}
        align="bottom"
        scopedHost={inspectScopedHost}
        zIndexClass="z-[10030]"
        overlayClassName={
          inspectScopedHost
            ? "bg-aera-black/40 rounded-[inherit]"
            : undefined
        }
        panelClassName={[
          inspectScopedHost
            ? "flex flex-col overflow-hidden rounded-t-3xl rounded-b-[inherit] border-t border-aera-lightgray bg-aera-white shadow-xl"
            : "flex flex-col overflow-hidden rounded-t-3xl rounded-b-none border-t border-aera-lightgray bg-aera-white shadow-xl",
          inspectScopedHost ? "max-h-[85%]" : "max-h-[80dvh]",
        ].join(" ")}
      >
        <div className="flex shrink-0 justify-center pb-1 pt-2">
          <div className="h-1 w-10 rounded-full bg-aera-lightgray" aria-hidden />
        </div>

        <header className="flex shrink-0 flex-col px-4 pb-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="text-xl font-bold text-aera-charcoal">
                {inspectTitle}
              </h2>
              <p className="mt-1 text-sm leading-relaxed text-aera-gray">
                {drawerPeriod ? formatPeriodThaiFull(drawerPeriod) : "—"}
              </p>
              <p className="mt-1 text-sm leading-relaxed text-aera-gray">
                {inspectHint}
              </p>
            </div>
            <ModalCloseButton
              onClose={handleInspectClose}
              className="-mr-1 shrink-0"
            />
          </div>
        </header>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-4">
          {workspaceId && drawerPeriod ? (
            <AccountingReviewContent
              workspaceId={workspaceId}
              serviceId={serviceId}
              period={drawerPeriod}
              classification={inspectClassification}
              returnTo={returnToRaw}
              embedded
              onClose={handleInspectClose}
            />
          ) : null}
        </div>

        <div className="shrink-0 space-y-3 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <AeraButton
            type="button"
            variant="outline"
            fontWeight="medium"
            fullWidth
            className="cursor-pointer rounded-full!"
            onClick={handleInspectClose}
          >
            ปิด
          </AeraButton>
        </div>
      </ModalShell>

      {variant === "drawer" ? (
        <AccountingPreparePanel
          onClose={() => onClose?.()}
          loading={loading && periods === null}
          issuesLoading={
            (drawerHandoffs === null || drawerFeedItems === null) && !error
          }
          error={error}
          periodOptions={drawerPeriodOptions}
          selectedPeriod={drawerPeriod || ""}
          onPeriodChange={setDrawerPeriod}
          readinessPercent={drawerReadinessPercent}
          readyCount={drawerReadyCount}
          needsActionCount={drawerNeedsAction}
          waitingCount={drawerWaiting}
          correctionCount={drawerCorrection}
          recordedCount={drawerPeriodRow?.recorded_count ?? 0}
          incomeCount={Number(drawerPeriodRow?.income_recorded_count ?? 0)}
          expenseCount={Number(drawerPeriodRow?.expense_recorded_count ?? 0)}
          needsReviewCount={drawerHandoffNeedsReview}
          missingEvidenceCount={drawerHandoffMissing}
          statusLabel={drawerStatusMeta.label}
          statusClassName={
            drawerStatusMeta.className || "bg-aera-offwhite text-aera-gray"
          }
          nextVersion={drawerNextVersion}
          latestVersion={drawerPeriodRow?.latest_export?.snapshot_version}
          busy={drawerBusy}
          canCreate={drawerCanCreate}
          createDisabledReason={drawerCreateReason}
          onPrepare={() => {
            if (drawerPeriod) void handlePrepare(drawerPeriod);
          }}
          onCreateFile={openDrawerExportPreview}
          onRefreshIssues={refreshDrawerIssues}
          issuesRefreshing={drawerFeedRefreshing}
          onInspect={(classification) =>
            openInspect(classification || "needs_review")
          }
          onViewList={handleViewList}
          detailsHref={accountingPageHref}
          exportRows={drawerExportRows}
          issueGroups={drawerIssueGroups}
        />
      ) : (
      <div className="flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto p-3 md:p-4">
        {!workspaceId ? (
          <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-3 py-18 text-center">
            <h3 className="mb-1 text-lg font-bold text-aera-charcoal">
              ไม่พบ workspace
            </h3>
            <p className="max-w-[400px] text-sm leading-relaxed text-aera-gray">
              เปิดหน้านี้จากหน้ารายการธุรกรรม
            </p>
          </div>
        ) : error ? (
          <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-3 py-18 text-center">
            <h3 className="mb-1 text-lg font-bold text-aera-charcoal">
              โหลดรอบบัญชีไม่สำเร็จ
            </h3>
            <p className="max-w-[400px] text-sm leading-relaxed text-aera-red">
              {error}
            </p>
          </div>
        ) : loading && periods === null ? (
          <div
            role="status"
            aria-live="polite"
            aria-busy="true"
            className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 py-18 text-aera-gray"
          >
            <Loader2 className="h-8 w-8 animate-spin text-aera-amber" aria-hidden />
            <p className="text-sm">กำลังโหลด…</p>
          </div>
        ) : !periods || periods.length === 0 ? (
          <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-3 py-18 text-center">
            <span className="mb-3 text-5xl opacity-50 grayscale" aria-hidden>
              🧾
            </span>
            <h3 className="mb-1 text-lg font-bold text-aera-charcoal">
              ยังไม่มีรอบบัญชี
            </h3>
            <p className="max-w-[400px] text-sm leading-relaxed text-aera-gray">
              เมื่อมีการบันทึกรายรับ/รายจ่าย รอบบัญชีรายเดือนจะแสดงที่นี่
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="overflow-hidden rounded-2xl border border-aera-lightgray/80 bg-aera-white">
              <div className="flex flex-wrap items-center gap-2 border-b border-aera-lightgray p-3 md:gap-3 md:p-4">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-aera-greenlight">
                  <CalendarDays
                    className="h-5 w-5 text-aera-green"
                    strokeWidth={2}
                    aria-hidden
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-aera-charcoal">
                    รอบบัญชี
                  </p>
                  <p className="text-xs tabular-nums text-aera-gray">
                    {filteredPeriods.length.toLocaleString("th-TH")} รอบ
                  </p>
                </div>
                <AccountingYearSelect
                  open={yearMenuOpen}
                  onOpenChange={setYearMenuOpen}
                  yearLabel={yearLabel}
                  yearValue={yearValue}
                  options={yearOptions}
                  onSelect={setSelectedYear}
                  className="ml-auto"
                />
              </div>

            {filteredPeriods.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <p className="text-sm font-semibold text-aera-charcoal">
                  ไม่มีรอบบัญชีในปีนี้
                </p>
                <p className="mt-1 text-xs text-aera-gray">
                  ลองเลือกปีอื่นจากเมนูปี
                </p>
              </div>
            ) : (
            <div className="overflow-x-auto overflow-y-visible">
              <table className="w-full min-w-[960px] text-sm">
                <thead>
                  <tr className="border-b border-aera-lightgray text-left text-xs text-aera-gray">
                    <th className="whitespace-nowrap px-4 py-3 font-semibold">รอบบัญชี</th>
                    <th className="whitespace-nowrap px-4 py-3 text-right font-semibold">บันทึกแล้ว</th>
                    <th className="whitespace-nowrap px-4 py-3 text-right font-semibold">พร้อมส่ง</th>
                    <th className="whitespace-nowrap px-4 py-3 text-right font-semibold">ต้องตรวจ</th>
                    <th className="whitespace-nowrap px-4 py-3 text-right font-semibold">เอกสารขาด</th>
                    <th className="whitespace-nowrap px-4 py-3 text-right font-semibold">แก้ไขหลังส่ง</th>
                    <th className="whitespace-nowrap px-4 py-3 font-semibold">สถานะล่าสุด</th>
                    <th className="whitespace-nowrap px-4 py-3 font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPeriods.map((p) => {
                    const busy = busyPeriod === p.period;
                    const exportingHere =
                      exportProgressOpen &&
                      exportProgress?.period === p.period
                        ? exportProgress
                        : null;
                    const nextVer = nextExportVersion(
                      p.period,
                      p.latest_export,
                      exports,
                    );
                    const failedExport =
                      !exportingHere && p.period_status === "export_failed"
                        ? exports.find(
                            (ex) =>
                              ex.period === p.period && ex.status === "failed",
                          )
                        : undefined;
                    const failedHint = failedExport
                      ? describeExportFailure(failedExport)
                      : null;
                    const statusForBadge = exportingHere
                      ? "exporting"
                      : p.period_status;
                    return (
                      <tr
                        key={p.period}
                        className="border-b border-aera-lightgray"
                      >
                        <td className="whitespace-nowrap px-4 py-3 font-semibold">
                          {formatPeriodThai(p.period)}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums font-semibold">
                          <span title={`รายรับ ${p.income_recorded_count ?? 0} · รายจ่าย ${p.expense_recorded_count ?? 0}`}>
                            {p.recorded_count.toLocaleString("th-TH")}
                          </span>
                          {(p.income_recorded_count != null ||
                            p.expense_recorded_count != null) && (
                            <div className="text-[10px] font-normal text-aera-gray tabular-nums">
                              รับ {Number(p.income_recorded_count ?? 0).toLocaleString("th-TH")}
                              {" · "}
                              จ่าย {Number(p.expense_recorded_count ?? 0).toLocaleString("th-TH")}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {p.ready_count > 0 ? (
                            <CountLink
                              href={reviewHref(p.period, "ready")}
                              title="เปิดรายการพร้อมส่งของรอบนี้"
                            >
                              {p.ready_count.toLocaleString("th-TH")}
                            </CountLink>
                          ) : (
                            <span className="tabular-nums">0</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {p.needs_review_count > 0 ? (
                            <CountLink
                              href={reviewHref(p.period, "needs_review")}
                              emphasize="amber"
                              title="เปิดรายการต้องตรวจของรอบนี้"
                            >
                              {p.needs_review_count.toLocaleString("th-TH")}
                            </CountLink>
                          ) : (
                            <span className="tabular-nums">0</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {p.missing_evidence_count > 0 ? (
                            <CountLink
                              href={reviewHref(p.period, "missing_evidence")}
                              emphasize="red"
                              title="เปิดรายการเอกสารขาดของรอบนี้"
                            >
                              {p.missing_evidence_count.toLocaleString("th-TH")}
                            </CountLink>
                          ) : (
                            <span className="tabular-nums">0</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums">
                          {p.correction_count.toLocaleString("th-TH")}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-row items-center gap-1">
                            <StatusBadge
                              status={statusForBadge}
                              exportVersion={
                                exportingHere
                                  ? exportingHere.nextVer
                                  : p.latest_export?.snapshot_version
                              }
                            />
                            {exportingHere ? (
                              <span
                                className={`text-[10px] font-semibold text-nowrap ${
                                  exportingHere.phase === "succeeded"
                                    ? "text-aera-green"
                                    : "text-aera-amber"
                                }`}
                              >
                                {exportingHere.phase === "succeeded"
                                  ? "เสร็จแล้ว"
                                  : `${
                                      EXPORT_PHASE_STEPS[
                                        EXPORT_PHASE_INDEX[exportingHere.phase]
                                      ]?.label ?? "กำลังสร้าง"
                                    }…`}{" "}
                                v{exportingHere.nextVer}
                              </span>
                            ) : (
                              <span className="text-[10px] font-semibold text-aera-gray text-nowrap">
                                ถัดไป v{nextVer}
                              </span>
                            )}
                            {failedHint ? (
                              <span
                                className="max-w-[220px] text-[10px] font-semibold leading-snug text-aera-red text-nowrap"
                                title={formatExportFailureToast(failedHint)}
                              >
                                {failedHint.message}
                                <span className="mt-0.5 block font-medium text-aera-gray">
                                  ถัดไป: {failedHint.nextAction}
                                </span>
                              </span>
                            ) : null}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-nowrap items-center gap-1.5">
                            <AeraButton
                              type="button"
                              variant="outline"
                              size="xxs"
                              fontWeight="semibold"
                              disabled={busy}
                              onClick={() => void handlePrepare(p.period)}
                              className="whitespace-nowrap"
                            >
                              เตรียมรอบ
                            </AeraButton>
                            <AeraButton
                              href={reviewHref(p.period)}
                              variant="outline"
                              size="xxs"
                              fontWeight="semibold"
                              className="whitespace-nowrap"
                            >
                              เปิดตรวจ
                            </AeraButton>
                            <AeraButton
                              href={tableHref(p.period, "bill-lin-income")}
                              target="_blank"
                              rel="noopener noreferrer"
                              variant="outline"
                              size="xxs"
                              fontWeight="semibold"
                              className="whitespace-nowrap"
                            >
                              ดูรายการ
                            </AeraButton>
                            <AeraButton
                              type="button"
                              variant="secondary"
                              size="xxs"
                              fontWeight="semibold"
                              disabled={busy}
                              onClick={() =>
                                void openExportPreview(p.period, p.latest_export)
                              }
                              className="whitespace-nowrap border-aera-green! bg-aera-green! text-aera-white! hover:opacity-90"
                              title={
                                exportingHere
                                  ? `กำลังสร้าง v${exportingHere.nextVer}…`
                                  : `ดูสรุปก่อนสร้าง v${nextVer} (ไม่ทับไฟล์เก่า) — Excel + ZIP ให้ผู้ทำบัญชี ยังไม่ใช่การลงบัญชี`
                              }
                              icon={
                                exportingHere ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : undefined
                              }
                            >
                              {exportingHere
                                ? `กำลังสร้าง v${exportingHere.nextVer}…`
                                : `สร้างไฟล์ v${nextVer}`}
                            </AeraButton>
                            {p.latest_export?.status === "succeeded" &&
                            p.latest_export.export_job_id ? (
                              <AeraButton
                                type="button"
                                variant="outline"
                                size="xxs"
                                fontWeight="semibold"
                                onClick={() =>
                                  handleDownload(p.latest_export!.export_job_id)
                                }
                                className="whitespace-nowrap"
                                icon={<Download size={12} />}
                              >
                                ดาวน์โหลด
                              </AeraButton>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-aera-lightgray bg-aera-offwhite/80">
                    <td className="whitespace-nowrap px-4 py-3 font-bold text-aera-charcoal">
                      รวม
                    </td>
                    <td className="px-4 py-3 text-right font-bold tabular-nums text-aera-charcoal">
                      {periodTotals.recorded.toLocaleString("th-TH")}
                    </td>
                    <td className="px-4 py-3 text-right font-bold tabular-nums text-aera-charcoal">
                      {periodTotals.ready.toLocaleString("th-TH")}
                    </td>
                    <td
                      className={`px-4 py-3 text-right font-bold tabular-nums ${
                        periodTotals.needsReview > 0
                          ? "text-aera-amber"
                          : "text-aera-charcoal"
                      }`}
                    >
                      {periodTotals.needsReview.toLocaleString("th-TH")}
                    </td>
                    <td
                      className={`px-4 py-3 text-right font-bold tabular-nums ${
                        periodTotals.missingEvidence > 0
                          ? "text-aera-red"
                          : "text-aera-charcoal"
                      }`}
                    >
                      {periodTotals.missingEvidence.toLocaleString("th-TH")}
                    </td>
                    <td className="px-4 py-3 text-right font-bold tabular-nums text-aera-charcoal">
                      {periodTotals.correction.toLocaleString("th-TH")}
                    </td>
                    <td className="px-4 py-3" colSpan={2} />
                  </tr>
                </tfoot>
              </table>
            </div>
            )}
            </div>

            {exports.length > 0 ? (
              <section className="overflow-hidden rounded-2xl border border-aera-lightgray/80 bg-aera-white">
                <div className="flex flex-wrap items-center gap-2 border-b border-aera-lightgray p-3 md:gap-3 md:p-4">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-aera-greenlight">
                    <History
                      className="h-5 w-5 text-aera-green"
                      strokeWidth={2}
                      aria-hidden
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h2 className="truncate text-sm font-semibold text-aera-charcoal">
                      ประวัติไฟล์ส่งบัญชี
                    </h2>
                    <p className="text-xs tabular-nums text-aera-gray">
                      {filteredExports.length.toLocaleString("th-TH")} ไฟล์ ·{" "}
                      {exportsByPeriod.length.toLocaleString("th-TH")} เดือน
                    </p>
                  </div>
                  <AccountingYearSelect
                    open={exportYearMenuOpen}
                    onOpenChange={setExportYearMenuOpen}
                    yearLabel={yearLabel}
                    yearValue={yearValue}
                    options={yearOptions}
                    onSelect={setSelectedYear}
                    className="ml-auto"
                  />
                </div>
                {exportsByPeriod.length === 0 ? (
                  <div className="px-4 py-8 text-center">
                    <p className="text-sm font-semibold text-aera-charcoal">
                      ไม่มีไฟล์ส่งบัญชีในปีนี้
                    </p>
                    <p className="mt-1 text-xs text-aera-gray">
                      ลองเลือกปีอื่นจากเมนูปี
                    </p>
                  </div>
                ) : (
                <div className="space-y-2 p-3 md:p-4">
                  {exportsByPeriod.map(({ period, items }) => {
                    const open = openExportPeriods.has(period);
                    const panelId = `export-period-${period}`;
                    return (
                      <div
                        key={period}
                        className="overflow-hidden rounded-xl border border-aera-lightgray bg-aera-white"
                      >
                        <AeraButton
                          type="button"
                          variant="transparent"
                          fullWidth
                          fontWeight="semibold"
                          textSize="sm"
                          paddingSize="md"
                          onClick={() => toggleExportPeriod(period)}
                          aria-expanded={open}
                          aria-controls={panelId}
                          className="justify-between! rounded-none! border-0 bg-aera-offwhite! px-4 py-3 hover:bg-aera-offwhite/80!"
                          icon={
                            <ChevronDown
                              size={16}
                              className={`shrink-0 text-aera-gray transition-transform duration-200 ${
                                open ? "rotate-0" : "-rotate-90"
                              }`}
                              aria-hidden
                            />
                          }
                          iconRight={
                            <span className="shrink-0 text-xs font-medium tabular-nums text-aera-gray">
                              {items.length.toLocaleString("th-TH")} version
                            </span>
                          }
                        >
                          <span className="min-w-0 flex-1 text-left text-aera-charcoal">
                            {formatPeriodThai(period)}
                          </span>
                        </AeraButton>
                        <div
                          id={panelId}
                          className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                            open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                          }`}
                          aria-hidden={!open}
                        >
                          <div className="min-h-0 overflow-hidden">
                            <div className="overflow-x-auto border-t border-aera-lightgray">
                              <table className="w-full min-w-[640px] text-sm">
                                <thead>
                                  <tr className="border-b border-aera-lightgray text-left text-xs text-aera-gray">
                                    <th className="whitespace-nowrap px-4 py-2.5 font-semibold">
                                      Version
                                    </th>
                                    <th className="whitespace-nowrap px-4 py-2.5 font-semibold">
                                      สร้างเมื่อ
                                    </th>
                                    <th className="whitespace-nowrap px-4 py-2.5 font-semibold">
                                      สถานะไฟล์
                                    </th>
                                    <th className="whitespace-nowrap px-4 py-2.5 font-semibold">
                                      ไฟล์
                                    </th>
                                    <th className="whitespace-nowrap px-4 py-2.5 font-semibold">
                                      Action
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {items.map((ex) => {
                                    const failInfo =
                                      ex.status === "failed" ||
                                      ex.status === "blocked"
                                        ? describeExportFailure(ex)
                                        : null;
                                    return (
                                      <tr
                                        key={ex.export_job_id}
                                        className="border-b border-aera-lightgray last:border-b-0"
                                      >
                                        <td className="whitespace-nowrap px-4 py-3 font-semibold tabular-nums">
                                          v{ex.snapshot_version}
                                          {ex.is_latest ? (
                                            <span className="ml-2 rounded-full bg-aera-greenlight px-2 py-0.5 text-[10px] font-semibold text-aera-green">
                                              ล่าสุด
                                            </span>
                                          ) : null}
                                        </td>
                                        <td className="whitespace-nowrap px-4 py-3 text-xs text-aera-gray">
                                          {formatExportWhen(
                                            ex.completed_at || ex.requested_at,
                                          )}
                                        </td>
                                        <td className="px-4 py-3">
                                          <div className="flex flex-col items-start gap-1">
                                            <StatusBadge
                                              status={
                                                ex.status === "succeeded"
                                                  ? "exported"
                                                  : ex.status === "failed" ||
                                                      ex.status === "blocked"
                                                    ? "export_failed"
                                                    : "exporting"
                                              }
                                            />
                                            {failInfo ? (
                                              <span
                                                className="max-w-[260px] text-[10px] font-semibold leading-snug text-aera-red"
                                                title={formatExportFailureToast(
                                                  failInfo,
                                                )}
                                              >
                                                {failInfo.message}
                                                <span className="mt-0.5 block font-medium text-aera-gray">
                                                  ถัดไป: {failInfo.nextAction}
                                                </span>
                                              </span>
                                            ) : null}
                                          </div>
                                        </td>
                                        <td className="px-4 py-3 text-xs text-aera-gray">
                                          <div className="max-w-[220px]">
                                            {ex.artifacts?.length
                                              ? ex.artifacts
                                                  .map((a) => a.filename)
                                                  .join(", ")
                                              : "—"}
                                          </div>
                                          <div className="mt-0.5 text-[10px] text-aera-gray">
                                            {adapterLabel(ex.adapter_key)}
                                            {ex.provider_reference
                                              ? ` · ref ${ex.provider_reference}`
                                              : ""}
                                          </div>
                                        </td>
                                        <td className="px-4 py-3">
                                          {ex.status === "succeeded" ? (
                                            <div className="flex flex-nowrap gap-1">
                              {(ex.artifacts?.length
                                ? ex.artifacts
                                : [
                                    {
                                      artifact_id: "",
                                      filename: "",
                                      size_bytes: 0,
                                    },
                                  ]
                              ).map((a) => (
                                <AeraButton
                                  key={a.artifact_id || "primary"}
                                  type="button"
                                  variant="outline"
                                  size="xxs"
                                  fontWeight="semibold"
                                  onClick={() =>
                                    handleDownload(
                                      ex.export_job_id,
                                      a.artifact_id || undefined,
                                    )
                                  }
                                  className="whitespace-nowrap"
                                  icon={<Download size={12} />}
                                >
                                  {a.filename.includes("Evidence")
                                    ? "หลักฐาน"
                                    : primaryDownloadLabel(
                                        ex.adapter_key,
                                        a.filename,
                                      )}
                                </AeraButton>
                              ))}
                            </div>
                          ) : failInfo?.actionKind ===
                            "review" ? (
                            <AeraButton
                              href={reviewHref(ex.period)}
                              variant="outline"
                              size="xxs"
                              fontWeight="semibold"
                              className="whitespace-nowrap"
                            >
                              เปิดตรวจ
                            </AeraButton>
                          ) : failInfo?.actionKind ===
                            "prepare" ? (
                            <AeraButton
                              type="button"
                              variant="outline"
                              size="xxs"
                              fontWeight="semibold"
                              disabled={busyPeriod === ex.period}
                              onClick={() => void handlePrepare(ex.period)}
                              className="whitespace-nowrap"
                            >
                              เตรียมรอบ
                            </AeraButton>
                          ) : failInfo?.actionKind ===
                            "retry_job" ? (
                            <AeraButton
                              type="button"
                              variant="outline"
                              size="xxs"
                              fontWeight="semibold"
                              disabled={busyPeriod === ex.period}
                              onClick={() =>
                                void handleRetryExport(
                                  ex.period,
                                  ex.export_job_id,
                                  ex.snapshot_version,
                                )
                              }
                              className="whitespace-nowrap"
                            >
                              ลองอีกครั้ง
                            </AeraButton>
                          ) : failInfo?.actionKind ===
                            "retry_create" ? (
                            <AeraButton
                              type="button"
                              variant="outline"
                              size="xxs"
                              fontWeight="semibold"
                              disabled={busyPeriod === ex.period}
                              onClick={() =>
                                void openExportPreview(
                                  ex.period,
                                  periods?.find(
                                    (row) => row.period === ex.period,
                                  )?.latest_export,
                                )
                              }
                              className="whitespace-nowrap"
                            >
                              สร้างไฟล์ใหม่
                            </AeraButton>
                          ) : (
                            <span className="text-xs text-aera-gray">
                              —
                            </span>
                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                )}
              </section>
            ) : null}
          </div>
        )}
      </div>
      )}
    </div>
    </EvidenceAccessProvider>
  );
}
